import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import { Ruler as RulerIcon, Undo2, Redo2, Image as ImageIcon, FolderOpen, Save as SaveIcon, Printer, Share2, User, LayoutDashboard, LogOut, Download, Link2, Mail, X, Menu, Folder, RefreshCw, Sliders, ShoppingCart } from 'lucide-react';
import { buildApiUrl } from '../config/api';
import { authHeaders } from '../config/api';
import styled, { keyframes } from 'styled-components';

import { useDesignContext } from '../hooks/useDesignContext';
import { encryptSharedDesignToUrlParam, isClientShareCryptoAvailable } from '../utils/shareLinkCrypto';
import RightPanel from './RightPanel';
import Popup from './Popup';
import { usePopup } from '../hooks/usePopup';
import PrintPreviewDialog from './PrintPreviewDialog';
import { renderProduction300DPI, renderProductionFromDesignJson300DPI, compressDataUrlToLimit, createCompressedPdf } from '../utils/productionRender';


const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const SimpleSpinner = styled.div<{ $color?: string }>`
  width: 18px;
  height: 18px;
  border: 2px solid ${props => props.$color || '#000'};
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

const ToolbarContainer = styled.div`
  min-height: 64px;
  background: #ffffff;
  color: #2d3748;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  position: sticky;
  top: 0;
  z-index: 100;
  gap: 4px;
  flex-wrap: nowrap;
  
  @media (max-width: 1200px) {
    gap: 4px;
    padding: 0 8px;
  }
  @media (max-width: 768px) {
    padding: 0; /* edge-to-edge on mobile */
    gap: 0;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding-left: 8px;
  padding-right: 8px;
  height: 64px;
  z-index: 20;
  background: #ffffff; 
  position: relative; /* Stays fixed while tools scroll */
  cursor: pointer;
  
  @media (max-width: 768px) {
    padding-left: 12px;
    padding-right: 8px;
    /* optional separator shadow */
    box-shadow: 4px 0 12px -4px rgba(0,0,0,0.05);
  }
`;

const ScrollableTools = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: flex-start;
  height: 100%;
  min-width: 0; /* Important for flex child scrolling */
  overflow-x: auto;
  scrollbar-width: none; /* Hide scrollbars for Firefox */
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbars for Chrome/Safari */
  }

  @media (max-width: 1200px) { gap: 6px; }

  @media (max-width: 768px) {
    display: none; /* Hidden on mobile, replaced by hamburger menu */
  }
`;

const MobileActionGroup = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto; /* Push to right */
    padding-right: 12px;
  }
`;

const MobileMenuToggle = styled.button`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: transparent;
    border: none;
    cursor: pointer;
    color: #2d3748;
  }
`;

const DrawerOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2999;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const DrawerSidebar = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 280px;
  max-width: 80vw;
  background: #ffffff;
  z-index: 3000;
  transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 4px 0 24px rgba(0,0,0,0.15);
  display: flex;
  flex-direction: column;
  padding: 20px;
  overflow-y: auto;
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
  
  font-size: 18px;
  font-weight: 800;
  color: #1a202c;
`;

const Logo = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #1a202c;
  display: flex;
  align-items: center;
  gap: 10px;
  @media (max-width: 1200px) {
    font-size: 16px;
    gap: 6px;
  }
  @media (max-width: 768px) {
    font-size: 0; /* Hide text on mobile */
    gap: 0;
  }
`;

const LogoSubtitle = styled.div`
  font-size: 11px;
  color: #718096;
  margin-top: 2px;
  @media (max-width: 1200px) {
    display: none;
  }
`;

const ToolGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
  height: 100%;
`;

const ToolButton = styled.button<{ $active?: boolean }>`
  background: ${props => props.$active ? '#5D722C' : '#e0e0e0'};
  color: ${props => props.$active ? '#ffffff' : '#333333'};
  border: 1px solid ${props => props.$active ? '#4a5b23' : '#d1d5db'};
  padding: 6px 10px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  box-sizing: border-box;
  flex-shrink: 0;

  svg {
    width: 14px !important;
    height: 14px !important;
  }
  
  &:hover {
    background: #5D722C;
    color: #ffffff;   /* ✅ font turns white on hover */
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
  
  &:active {
    transform: translateY(0);
  }
  span { white-space: nowrap; }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:disabled:hover {
    background: #e0e0e0;
    color: #333333;
    transform: none;
    box-shadow: none;
  }
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'success' | 'warning' | 'info' | 'purple' | 'pink' | 'red' | 'teal' | 'amber' }>`
  background: ${props => {
    switch (props.$variant) {
      case 'success': return '#5D722C'; // Monu Green
      case 'warning': return '#666666'; // Gray
      case 'info': return '#5D722C'; // Green
      case 'purple': return '#666666'; // Gray
      case 'pink': return '#666666'; // Gray
      case 'red': return '#dc2626'; // Red
      case 'teal': return '#5D722C'; // Green
      case 'amber': return '#f59e0b'; // Amber Gold
      default: return '#666666'; // Gray
    }
  }};
  color: ${props => props.$variant === 'amber' ? '#000000' : '#ffffff'};
  border: none;
  padding: 6px 10px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  margin-left: 6px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  box-sizing: border-box;
  flex-shrink: 0;

  svg {
    width: 14px !important;
    height: 14px !important;
  }
  
  &:hover {
    filter: brightness(0.95);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  
  &:active {
    transform: translateY(0);
  }
  span { white-space: nowrap; }
`;

const DrawerItem = styled.button<{ $active?: boolean; $variant?: string }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${props => props.$active ? '#f0fdf4' : 'transparent'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: ${props => {
    if (props.$variant === 'red') return '#dc2626';
    if (props.$variant === 'primary') return '#2563eb';
    if (props.$active) return '#166534';
    return '#334155';
  }};
  font-size: 15px;
  font-weight: 600;
  transition: all 0.2s ease;
  margin-bottom: 4px;

  &:hover {
    background: ${props => props.$active ? '#dcfce7' : '#f1f5f9'};
    transform: translateX(4px);
  }

  svg {
    color: ${props => {
    if (props.$variant === 'red') return '#dc2626';
    if (props.$variant === 'primary') return '#2563eb';
    if (props.$active) return '#166534';
    return '#64748b';
  }};
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: #e2e8f0;
  margin: 0 12px;
`;




const Toolbar: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTools, setActiveTools] = useState<Set<string>>(new Set());
  const {
    currentDesign,
    updateDesign,
    saveDesign,
    exportDesign,
    undo,
    redo,
    canUndo,
    canRedo,
    stageRef,
    selectedBackground,
    setSelectedBackground,
    setPrintSettings,
    selectedGraniteMeta,
    selectedGraniteImage,
    selectedGraniteBaseImage,
    setSelectedGraniteMeta,
    setSelectedGraniteImage,
    isFlatMarkerCurrent,
    currentTool,
    lastChangeContext,
    activeSide,
    setActiveSide,
    authedUser,
    setAuthedUser,
    isDealerSalesperson,
    canCreateQuotation,
    isApplyingDesign,
    isGlobalLoading,
    setIsGlobalLoading,
    setLoadingMessage,
    designMode,
  } = useDesignContext();

  const isAuthed = !!authedUser;
  const { popupState, hidePopup, showError, showSuccess, showInfo, showConfirm } = usePopup();
  const openInputRef = useRef<HTMLInputElement | null>(null);

  const [bgOpen, setBgOpen] = useState(false);
  const [bgList, setBgList] = useState<any[]>([]);
  const [bgLoading, setBgLoading] = useState(false);
  const [bgError, setBgError] = useState<string | null>(null);
  const [bgSelectedIdx, setBgSelectedIdx] = useState<number | null>(null);

  const [shareOpen, setShareOpen] = useState(false);
  const [shareBusy, setShareBusy] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportTarget, setExportTarget] = useState<{ id?: number; name?: string; thumbnail_url?: string; design_json?: any } | null>(null);
  const [exportPreview, setExportPreview] = useState<string | null>(null);
  const [exportFormat, setExportFormat] = useState<'png' | 'jpg' | 'pdf' | 'kpro'>('png');
  // Email modal state
  const [shareEmailOpen, setShareEmailOpen] = useState(false);
  const [shareEmailTo, setShareEmailTo] = useState('');
  const [shareEmailMsg, setShareEmailMsg] = useState('');
  const [shareEmailSending, setShareEmailSending] = useState(false);
  const [shareEmailThumb, setShareEmailThumb] = useState<string | null>(null);
  const [shareEmailThumbBack, setShareEmailThumbBack] = useState<string | null>(null);

  const toolKey = String(currentTool || '').toLowerCase();
  const isModelToolActive = toolKey === 'model' || toolKey === 'shapes';
  const isModelLastChange = String(lastChangeContext || '').toLowerCase() === 'model';


  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState<string>('');
  const [saveThumb, setSaveThumb] = useState<string | undefined>(undefined);
  const [saveBackThumb, setSaveBackThumb] = useState<string | undefined>(undefined);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveAndCartBusy, setSaveAndCartBusy] = useState(false);

  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [editMenuOpen, setEditMenuOpen] = useState(false);
  const [guideDialogOpen, setGuideDialogOpen] = useState(false);

  const [guideTopIn, setGuideTopIn] = useState<number>(() => {
    try { return Number(localStorage.getItem('guides.topIn') || '0.75'); } catch { return 0.75; }
  });
  const [guideLeftIn, setGuideLeftIn] = useState<number>(() => {
    try { return Number(localStorage.getItem('guides.leftIn') || '0.75'); } catch { return 0.75; }
  });
  const [guideBottomIn, setGuideBottomIn] = useState<number>(() => {
    try { return Number(localStorage.getItem('guides.bottomIn') || '0.75'); } catch { return 0.75; }
  });
  const [guideRightIn, setGuideRightIn] = useState<number>(() => {
    try { return Number(localStorage.getItem('guides.rightIn') || '0.75'); } catch { return 0.75; }
  });

  const isBackSideSupported = useMemo(() => {
    const modelName = String(currentDesign?.selectedModelName || '').toLowerCase();
    const modelId = Number(currentDesign?.selectedModelId || 0);
    const sizeName = String(currentDesign?.selectedSizeName || '').replace(/\s+/g, '');

    if (modelId === 3 || modelName.includes('serptop')) {
      const supportedSizes = ['24x6x24/36x12x6', '36x6x24/48x12x6', '48x6x24/66x12x6', '54x8x28/72x14x8'];
      return supportedSizes.some(s => sizeName.includes(s.split('/')[0].replace(/\s+/g, '')));
    }
    if (modelId === 5 || modelName.includes('tear drop') || modelName.includes('teardrop')) {
      return sizeName.includes('24x6x28/36x12x6'.split('/')[0].replace(/\s+/g, ''));
    }
    if (modelId === 4 || modelName.includes('single heart')) {
      return sizeName.includes('24x6x28/36x12x6'.split('/')[0].replace(/\s+/g, ''));
    }
    return false;
  }, [currentDesign?.selectedModelName, currentDesign?.selectedModelId, currentDesign?.selectedSizeName]);

  const [guidePreview, setGuidePreview] = useState<boolean>(true);

  const handleBackgroundClick = async () => {
    setBgOpen(true);
    setBgLoading(true);
    setBgError(null);
    try {
      // Get current model information
      const modelId = selectedGraniteMeta?.modelId || currentDesign?.selectedModelId || 0;
      const sizeId = selectedGraniteMeta?.sizeId || currentDesign?.selectedSizeId || 0;
      const colorId = selectedGraniteMeta?.colorId || currentDesign?.selectedColorId || 0;

      const res = await fetch(buildApiUrl(`/api/backgrounds?modelId=${modelId}&sizeId=${sizeId}&colorId=${colorId}&designMode=${encodeURIComponent(designMode || 'LSP/QD')}`));
      const data = await res.json();
      const rawList = Array.isArray(data?.backgrounds) ? data.backgrounds : [];
      // Keep image backgrounds in the list; solid default color is rendered as a dedicated fixed card.
      const list = rawList.filter((bg: any) => Boolean(bg?.image_url || bg?.thumb_url));
      setBgList(list);

      let defaultIdx: number | null = null;
      // Check if current background is the solid color one
      if (selectedBackground && (selectedBackground as any).background_color === '#E0E0E0' && !selectedBackground.image_url) {
        setBgSelectedIdx(-1);
      } else {
        // Set default to first background
        defaultIdx = list.length > 0 ? 0 : null;
        setBgSelectedIdx(defaultIdx);
      }

      try {
        if (!selectedBackground && defaultIdx != null && list[defaultIdx]) {
          setSelectedBackground(list[defaultIdx]);
          try { localStorage.setItem('design_tool.background', JSON.stringify(list[defaultIdx])); } catch { }
        }
      } catch { }
    } catch (e: any) {
      setBgError('Failed to load backgrounds');
    } finally {
      setBgLoading(false);
    }
  };

  const generatePreviewThumbnail = async () => {
    const stage = stageRef?.current;
    if (!stage) return undefined;

    let stageSnapshot = '';
    const tmpContainer = document.createElement('div');
    tmpContainer.style.position = 'fixed';
    tmpContainer.style.left = '-10000px';
    tmpContainer.style.top = '-10000px';
    document.body.appendChild(tmpContainer);
    try {
      const cloned = stage.clone({ container: tmpContainer });
      try {
        cloned.find('Transformer').forEach((transformer: any) => { transformer.visible(false); });
        cloned.find('Group').forEach((group: any) => {
          if (group.attrs.name === 'selection-group' || group.attrs.name === 'ui-overlay') {
            group.visible(false);
          }
        });
        cloned.find('Rect').forEach((rect: any) => {
          if (rect.attrs.fill === 'rgba(59, 130, 246, 0.2)' || rect.attrs.stroke === 'rgba(59, 130, 246, 0.8)') {
            rect.visible(false);
          }
        });
        cloned.find('Shape').forEach((shape: any) => {
          try {
            const stroke = String(shape?.attrs?.stroke || '').toLowerCase();
            const dash = shape?.attrs?.dash;
            const strokeWidth = Number(shape?.attrs?.strokeWidth || 0);
            const isSelectionStroke = strokeWidth >= 2 && (
              stroke === '#007bff' ||
              stroke === '#3b82f6' ||
              stroke.includes('59, 130, 246') ||
              stroke.includes('59,130,246') ||
              stroke.includes('0, 123, 255') ||
              stroke.includes('0,123,255')
            );
            const hasDash = Array.isArray(dash) && dash.length >= 2;
            if (hasDash && strokeWidth > 0) {
              try {
                if (typeof shape?.setAttrs === 'function') {
                  shape.setAttrs({ dash: undefined, stroke: isSelectionStroke ? undefined : shape?.attrs?.stroke, strokeWidth: isSelectionStroke ? 0 : strokeWidth });
                } else {
                  shape.attrs = { ...(shape.attrs || {}), dash: undefined, stroke: isSelectionStroke ? undefined : shape?.attrs?.stroke, strokeWidth: isSelectionStroke ? 0 : strokeWidth };
                }
              } catch { }
            }
          } catch { }
        });
        try {
          const guidesLayer = cloned.findOne && (cloned.findOne('#guides-layer') || cloned.findOne('.guides-layer'));
          if (guidesLayer) guidesLayer.visible(false);
        } catch { }

        // Ensure all image elements (high-res porcelain, artwork, etc) are copied from stage and fully loaded
        try {
          const origImages = stage.find('Image');
          const konvaImages = cloned.find('Image');
          const imgPromises: Promise<void>[] = [];

          konvaImages.forEach((kImg: any, idx: number) => {
            const origImgNode = origImages[idx];
            if (origImgNode) {
              const origImgObj = origImgNode.image();
              if (origImgObj && (!kImg.image() || !kImg.image().complete)) {
                kImg.image(origImgObj);
              }
            }
            const imgObj = kImg.image();
            if (imgObj && !imgObj.complete) {
              imgPromises.push(new Promise<void>((resolve) => {
                imgObj.onload = () => resolve();
                imgObj.onerror = () => resolve();
                setTimeout(resolve, 3000);
              }));
            }
          });
          if (imgPromises.length > 0) {
            await Promise.all(imgPromises);
          }
        } catch { }

        stageSnapshot = cloned.toDataURL({ pixelRatio: 1.25 });
      } finally {
        cloned.destroy();
      }
    } finally {
      try { document.body.removeChild(tmpContainer); } catch { }
    }

    // compose with selected background if present
    try {
      const bgObj = selectedBackground || null;
      const bgUrl = typeof bgObj === 'string' ? bgObj : (bgObj && (bgObj.image_url || (bgObj as any).src));
      if (bgUrl) {
        const imgStage = new Image();
        const imgBg = new Image();
        await new Promise<void>((resolve) => { imgStage.onload = () => resolve(); imgStage.onerror = () => resolve(); imgStage.src = stageSnapshot; });
        await new Promise<void>((resolve) => { imgBg.crossOrigin = 'anonymous'; imgBg.onload = () => resolve(); imgBg.onerror = () => resolve(); imgBg.src = bgUrl; });
        const outW = Math.max(1, stage.width ? stage.width() : 1200);
        const outH = Math.max(1, stage.height ? stage.height() : 600);
        const canvas = document.createElement('canvas');
        canvas.width = outW; canvas.height = outH;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          try {
            (ctx as any).imageSmoothingEnabled = true;
            (ctx as any).imageSmoothingQuality = 'high';
          } catch { }
          // draw bg to cover
          try {
            const rBg = imgBg.width / imgBg.height;
            const rCv = outW / outH;
            let dw = outW, dh = outH, dx = 0, dy = 0;
            if (rBg > rCv) {
              dh = outH; dw = Math.ceil(outH * rBg); dx = Math.floor((outW - dw) / 2);
            } else {
              dw = outW; dh = Math.ceil(outW / rBg); dy = Math.floor((outH - dh) / 2);
            }
            ctx.drawImage(imgBg, dx, dy, dw, dh);
          } catch { }
          ctx.drawImage(imgStage, 0, 0, outW, outH);
          return encodeCanvasJpegTarget(canvas, 245 * 1024);
        } else {
          return stageSnapshot;
        }
      } else {
        try {
          const imgStage = new Image();
          await new Promise<void>((resolve) => { imgStage.onload = () => resolve(); imgStage.onerror = () => resolve(); imgStage.src = stageSnapshot; });
          const outW = Math.max(1, stage.width ? stage.width() : 1200);
          const outH = Math.max(1, stage.height ? stage.height() : 600);
          const canvas = document.createElement('canvas');
          canvas.width = outW; canvas.height = outH;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            try {
              (ctx as any).imageSmoothingEnabled = true;
              (ctx as any).imageSmoothingQuality = 'high';
            } catch { }
            ctx.drawImage(imgStage, 0, 0, outW, outH);
            return encodeCanvasJpegTarget(canvas, 245 * 1024);
          } else {
            return stageSnapshot;
          }
        } catch {
          return stageSnapshot;
        }
      }
    } catch {
      return stageSnapshot;
    }
  };

  const handleSaveClick = async () => {
    try {
      setSaveBusy(true);
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      if (!token) {
        showInfo('Sign In Required', 'Please sign in to save your design.');
        setRightPanelOpen(true);
        return;
      }

      const pdLastRequired = extractPersonalLastName();
      if (!pdLastRequired) {
        showError('Personal Details', 'Please enter your Personal Details before saving.');
        return;
      }
      // Prepare name and preview thumbnail, open Save modal
      let defaultName = (currentDesign as any)?.name || 'New Design';
      const designId = (currentDesign as any)?.id;
      const extractBaseName = (value: string): string => {
        const raw = (value || '').toString().trim();
        const m = raw.match(/^(.+?)[-_ ](\d+)$/);
        return (m?.[1] ?? raw).toString().trim();
      };

      const pdLast = extractPersonalLastName();
      const cleanedLast = (pdLast || '').replace(/[^a-zA-Z0-9_ -]/g, '').trim();
      const currentBase = extractBaseName(defaultName);
      const shouldUseLastName = Boolean(
        cleanedLast && (
          (!designId && (defaultName === 'New Design' || !defaultName)) ||
          (designId && currentBase.toLowerCase() !== cleanedLast.toLowerCase())
        )
      );

      if (shouldUseLastName) {
        defaultName = cleanedLast;
      }

      // If using last name (new design or last name changed), ensure the suggested name is unique.
      if (shouldUseLastName) {
        try {
          const res = await fetch(buildApiUrl('/api/designs'), { headers: { ...authHeaders() } });
          if (res.ok) {
            const data = await res.json().catch(() => ({}));
            const list = Array.isArray(data?.designs) ? data.designs : (Array.isArray(data?.data) ? data.data : []);
            const otherDesigns = list.filter((d: any) => String(d?.id ?? '') !== String(designId ?? ''));

            const prefix = cleanedLast || 'NewDesign';
            const escapedPrefix = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const baseExists = otherDesigns.some((d: any) => String(d?.name || '').trim().toLowerCase() === prefix.toLowerCase());
            if (!baseExists) {
              defaultName = prefix;
            } else {
              const prefixRegex = new RegExp(`^(${escapedPrefix}|New[-_ ]?Design)[-_ ]?(\\d+)$`, 'i');
              let max = 0;
              for (const d of otherDesigns) {
                const name = String(d?.name || '').trim();
                const m = name.match(prefixRegex);
                if (m && m[2]) {
                  const n = parseInt(m[2].replace(/^0+/, '') || '0', 10) || 0;
                  if (n > max) max = n;
                }
              }
              const next = (max || 0) + 1;
              const padded = next < 10 ? `0${next}` : `${next}`;
              defaultName = `${prefix}_${padded}`;
            }
          }
        } catch (err) {
        }
      }

      setSaveName(defaultName);

      // Multi-side thumbnail generation logic
      const originalSide = activeSide;

      // 1. Capture current side
      const thumbCurrent = await generatePreviewThumbnail();
      if (originalSide === 'front') {
        setSaveThumb(thumbCurrent);
      } else {
        setSaveBackThumb(thumbCurrent);
      }

      // 2. Capture other side if supported
      if (isBackSideSupported) {
        const otherSide = originalSide === 'front' ? 'back' : 'front';
        setActiveSide(otherSide);
        // Wait for state update, Konva redraw, and asset loading
        await new Promise(resolve => setTimeout(resolve, 600));
        const thumbOther = await generatePreviewThumbnail();
        if (otherSide === 'front') {
          setSaveThumb(thumbOther);
        } else {
          setSaveBackThumb(thumbOther);
        }
        // Restore original side
        setActiveSide(originalSide);
        await new Promise(resolve => setTimeout(resolve, 200));
      } else {
        setSaveBackThumb(undefined);
      }

      setSaveOpen(true);
    } catch (e: any) {
      showError('Save Error', e?.message || 'Failed to save');
    } finally {
      setSaveBusy(false);
    }
  };

  const uploadPendingImages = useCallback(async () => {
    try {
      const els = (currentDesign as any)?.elements || [];
      const toUpload: Array<{ el: any; index: number; dataUrl: string }> = [];
      els.forEach((el: any, idx: number) => {
        const src = el?.properties?.src;
        if (typeof src === 'string' && src.startsWith('data:')) {
          toUpload.push({ el, index: idx, dataUrl: src });
        }
      });

      if (toUpload.length === 0) return;

      for (const item of toUpload) {
        try {
          const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token') || '';
          const res = await fetch(buildApiUrl('/api/profile/photo'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': 'Bearer ' + token } : {}) },
            body: JSON.stringify({ photo_data_url: item.dataUrl })
          });
          const json = await res.json().catch(() => ({}));
          if (res.ok && json && json.url) {
            updateDesign((prev: any) => {
              const nextEls = [...(prev.elements || [])];
              if (nextEls[item.index]) {
                nextEls[item.index] = { ...nextEls[item.index], properties: { ...nextEls[item.index].properties, src: json.url } };
              }
              return { ...prev, elements: nextEls };
            });
            try { if ((currentDesign as any)?.elements && (currentDesign as any).elements[item.index]) { (currentDesign as any).elements[item.index].properties.src = json.url; } } catch { }
          } else {
            throw new Error(json?.error || `Failed to upload image ${item.index + 1}`);
          }
        } catch (err) {
          const msg = (err as any)?.message || 'Failed to upload one of the images before save.';
          throw new Error(msg);
        }
      }
    } catch (err) {
      throw err;
    }
  }, [currentDesign, updateDesign]);

  const handleSaveAndAddToCartClick = async () => {
    try {
      setSaveAndCartBusy(true);
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      if (!token) {
        showInfo('Sign In Required', 'Please sign in to save your design and move to quotation creation.');
        setRightPanelOpen(true);
        return;
      }

      const pdLastRequired = extractPersonalLastName();
      if (!pdLastRequired) {
        showError('Personal Details', 'Please enter your Personal Details before saving.');
        return;
      }

      if (setIsGlobalLoading) {
        setIsGlobalLoading(true);
        if (setLoadingMessage) setLoadingMessage('Saving design & adding to cart...');
      }

      (window as any).__internalReload = true;

      let defaultName = (currentDesign as any)?.name || 'New Design';
      const designId = (currentDesign as any)?.id;
      const pdLast = extractPersonalLastName();
      const cleanedLast = (pdLast || '').replace(/[^a-zA-Z0-9_ -]/g, '').trim();
      if (cleanedLast && (!designId || defaultName === 'New Design')) {
        defaultName = cleanedLast;
      }

      // 1. Generate preview thumbnails FIRST while all images are loaded on the live canvas
      const originalSide = activeSide;
      const thumbCurrent = await generatePreviewThumbnail();
      let frontThumb = originalSide === 'front' ? thumbCurrent : undefined;
      let backThumb = originalSide === 'back' ? thumbCurrent : undefined;

      if (isBackSideSupported) {
        const otherSide = originalSide === 'front' ? 'back' : 'front';
        setActiveSide(otherSide);
        await new Promise(resolve => setTimeout(resolve, 600));
        const thumbOther = await generatePreviewThumbnail();
        if (otherSide === 'front') frontThumb = thumbOther;
        else backThumb = thumbOther;
        setActiveSide(originalSide);
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // 2. Upload pending base64 images after capturing the full-resolution thumbnail
      await uploadPendingImages();

      const buildDesignJsonForSave = (opts: { name: string; id?: any }) => {
        const next: any = {
          ...currentDesign,
          ...opts,
          background: selectedBackground,
          selectedGraniteBaseImage: selectedGraniteBaseImage || (currentDesign as any)?.selectedGraniteBaseImage || null,
          selectedSizeName: (currentDesign as any)?.selectedSizeName,
          selectedGraniteMeta: selectedGraniteMeta || (currentDesign as any)?.selectedGraniteMeta
        };
        return next;
      };

      const designJson = buildDesignJsonForSave({ name: defaultName, id: designId });
      const saveData = {
        name: defaultName,
        design_json: designJson,
        thumbnail_data_url: frontThumb,
        back_thumbnail_data_url: backThumb,
        model_id: currentDesign.selectedModelId || null,
        background_id: selectedBackground?.id || null,
        canvas_width: 1200,
        canvas_height: 600,
        status: 'saved'
      };

      const executeSaveAndCart = async (forceSaveAsNew: boolean) => {
        let savedId = forceSaveAsNew ? undefined : designId;
        if (savedId) {
          const res = await fetch(buildApiUrl(`/api/designs/${savedId}`), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify(saveData)
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || data.ok === false) throw new Error(data.error || 'Failed to update design');
          if (data.is_new_design && data.id) {
            savedId = data.id;
            updateDesign((prev: any) => ({ ...prev, id: savedId, is_quoted: false, status: 'saved' }));
          }
        } else {
          const res = await fetch(buildApiUrl('/api/designs'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify(saveData)
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || data.ok === false) throw new Error(data.error || 'Failed to save design');
          savedId = data.id || data.design?.id;
          if (savedId) {
            updateDesign((prev: any) => ({ ...prev, id: savedId, is_quoted: false, status: 'saved' }));
          }
        }

        if (!canCreateQuotation) {
          if (setIsGlobalLoading) setIsGlobalLoading(false);
          showSuccess('Design Saved', forceSaveAsNew ? 'Saved as a new design.' : 'Your design has been saved successfully.');
          return;
        }

        showSuccess('Saved & Added to Cart', forceSaveAsNew ? 'Saved as new design. Redirecting...' : 'Redirecting to quotation creation...');
        setTimeout(() => {
          (window as any).__internalReload = true;
          window.location.href = `/customer/dashboard?tab=saved_designs&checkout=${savedId || ''}`;
        }, 400);
      };

      const isDesignQuoted = (currentDesign as any)?.status === 'quoted' || (currentDesign as any)?.is_quoted;

      if (designId && isDesignQuoted) {
        // Already quoted: force save as new design
        await executeSaveAndCart(true);
      } else if (designId) {
        // Unquoted existing design: ask user whether to update or save as new
        if (setIsGlobalLoading) setIsGlobalLoading(false);
        showConfirm(
          'Update Existing Design?',
          'This design already exists in your saved designs. Do you want to update the existing design or save it as a new design?',
          async () => {
            try {
              if (setIsGlobalLoading) setIsGlobalLoading(true);
              await executeSaveAndCart(false);
            } catch (e: any) {
              if (setIsGlobalLoading) setIsGlobalLoading(false);
              showError('Save & Cart Error', e?.message || 'Failed to update design');
            }
          },
          async () => {
            try {
              if (setIsGlobalLoading) setIsGlobalLoading(true);
              await executeSaveAndCart(true);
            } catch (e: any) {
              if (setIsGlobalLoading) setIsGlobalLoading(false);
              showError('Save & Cart Error', e?.message || 'Failed to save new design');
            }
          },
          'Update Existing',
          'Save as New'
        );
      } else {
        await executeSaveAndCart(false);
      }
    } catch (e: any) {
      if (setIsGlobalLoading) setIsGlobalLoading(false);
      showError('Save & Cart Error', e?.message || 'Failed to save design');
    } finally {
      setSaveAndCartBusy(false);
    }
  };

  const handleLogoutClick = () => {
    (window as any).__internalReload = true;
    void fetch(buildApiUrl('/api/auth/logout'), {
      method: 'POST',
      headers: { ...authHeaders() },
      keepalive: true
    }).catch(() => { });

    try {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.setItem('logout_signal', Date.now().toString());
    } catch { }
    try { sessionStorage.removeItem('auth_token'); sessionStorage.removeItem('auth_user'); } catch { }
    window.dispatchEvent(new Event('auth:logout'));
  };

  React.useEffect(() => {
    const onExportOpen = async (e: any) => {
      const id = e?.detail?.designId;
      if (!id) return;
      try {
        const r = await fetch(buildApiUrl(`/api/designs/${id}`), { headers: { ...authHeaders() } });
        const j = await r.json().catch(() => ({}));
        if (r.ok && j?.ok) {
          setExportTarget({ id, name: j.design?.name, thumbnail_url: j.design?.thumbnail_url });
          setExportPreview(j.design?.thumbnail_url || null);
          setExportFormat('png');
          setExportOpen(true);
        }
      } catch { }
    };
    window.addEventListener('export:open', onExportOpen);
    return () => window.removeEventListener('export:open', onExportOpen);
  }, []);

  const loadCdnScript = (src: string) => new Promise<void>((resolve, reject) => {
    const el = document.createElement('script');
    el.src = src; el.async = true; el.onload = () => resolve(); el.onerror = reject; document.head.appendChild(el);
  });

  // Helpers to extract Personal Details fields from the canvas elements
  const getTextContent = (elements: any[], targetId: string): string => {
    if (!Array.isArray(elements)) return '';
    const match = elements.find((el: any) => el?.id === targetId || el?.name === targetId);
    const raw = match?.properties?.content ?? match?.properties?.text ?? match?.text ?? '';
    return typeof raw === 'string' ? raw : '';
  };

  const extractPersonalLastName = (): string => {
    try {
      const els = (currentDesign as any)?.elements || [];
      let fallbackFirstName = '';

      for (const el of els) {
        if (!el || typeof el !== 'object') continue;
        const props = (el as any).properties || {};
        const fieldName = String(props.fieldName ?? props.field_name ?? '').trim().toLowerCase();
        const id = String((el as any).id ?? '').trim().toLowerCase();
        const name = String((el as any).name ?? '').trim().toLowerCase();

        const isLastNameField =
          fieldName === 'lastname' || fieldName === 'last_name' ||
          id === 'lastname' || id === 'last_name' ||
          name === 'lastname' || name === 'last_name';

        const isFullNameField =
          fieldName === 'fullname' || fieldName === 'full_name' ||
          id === 'fullname' || id === 'full_name' ||
          name === 'fullname' || name === 'full_name';

        const isFirstNameField =
          fieldName === 'firstname' || fieldName === 'first_name' ||
          id === 'firstname' || id === 'first_name' ||
          name === 'firstname' || name === 'first_name';

        const raw = props.content ?? props.text ?? (el as any).text ?? '';
        const value = String(raw || '').trim();

        if (isLastNameField) {
          if (!value || /^last\s*name$/i.test(value) || /^lastname$/i.test(value) || /^[FLM][.\s]*NAME$/i.test(value)) continue;
          return value; // Direct hit
        }

        if (isFullNameField) {
          if (!value || /^full\s*name$/i.test(value) || /^fullname$/i.test(value) || /^[FLM][.\s]*NAME$/i.test(value)) continue;
          const parts = value.split(/\s+/);
          if (parts.length > 0) {
            const lastPart = parts[parts.length - 1];
            if (/^[FLM][.\s]*NAME$/i.test(lastPart)) continue;
            return lastPart;
          }
        }

        if (isFirstNameField) {
          if (!value || /^first\s*name$/i.test(value) || /^firstname$/i.test(value) || /^[FLM][.\s]*NAME$/i.test(value)) continue;
          fallbackFirstName = value; // Save to fallback
        }
      }

      // If we didn't find a direct LastName or FullName, we likely hit an ESQ template
      // where the "Full Name" is stuffed into the "FirstName" field.
      if (fallbackFirstName) {
        const parts = fallbackFirstName.split(/\s+/);
        if (parts.length > 0) {
          const lastPart = parts[parts.length - 1];
          if (/^[FLM][.\s]*NAME$/i.test(lastPart)) return '';
          return lastPart;
        }
      }

      return '';
    } catch {
      return '';
    }
  };

  const estimateDataUrlBytes = (dataUrl: string): number => {
    try {
      const i = dataUrl.indexOf(',');
      if (i < 0) return dataUrl.length;
      const b64 = dataUrl.slice(i + 1);
      const padding = (b64.endsWith('==') ? 2 : (b64.endsWith('=') ? 1 : 0));
      return Math.floor((b64.length * 3) / 4) - padding;
    } catch {
      return dataUrl.length;
    }
  };

  const encodeCanvasJpegTarget = (canvas: HTMLCanvasElement, targetBytes: number): string => {
    // Avoid multiple synchronous toDataURL() passes; each pass can block the UI.
    const primary = canvas.toDataURL('image/jpeg', 0.8);
    if (estimateDataUrlBytes(primary) <= targetBytes) return primary;
    return canvas.toDataURL('image/jpeg', 0.72);
  };

  const generateImageFromDesign = async (designData: any, opts: { format: 'png' | 'jpg'; size: 'small' | 'large' }) => {
    const graniteConfig = (window as any).__appGraniteConfig;
    const designMode = (window as any).__appDesignMode || '';
    return renderProductionFromDesignJson300DPI(
      designData,
      graniteConfig,
      designMode,
      {
        format: opts.format === 'jpg' ? 'jpeg' : 'png',
        quality: opts.format === 'jpg' ? 0.88 : 1,
        targetDPI: opts.size === 'small' ? 150 : 300
      }
    );
  };

  const performExportDownload = async () => {
    const target = exportTarget; if (!target) return;
    try {
      let designData: any = target.design_json || {};
      let name = target.name || 'design';

      if (target.id) {
        try {
          const r = await fetch(buildApiUrl(`/api/designs/${target.id}`), { headers: { ...authHeaders() } });
          const j = await r.json().catch(() => ({}));
          if (r.ok && j?.ok) {
            designData = typeof j.design?.design_json === 'string' ? JSON.parse(j.design.design_json) : (j.design?.design_json || designData);
            name = j.design?.name || name;
          }
        } catch { }
      }

      const isCurrentActive = Boolean(stageRef?.current && currentDesign?.id && String(currentDesign.id) === String(target.id));

      if (exportFormat === 'png' || exportFormat === 'jpg') {
        let dataURL = '';
        if (isCurrentActive) {
          const graniteConfig = (window as any).__appGraniteConfig;
          const designMode = (window as any).__appDesignMode || '';
          dataURL = await renderProduction300DPI(
            stageRef.current,
            graniteConfig,
            designMode,
            currentDesign.elements,
            {
              format: exportFormat === 'jpg' ? 'jpeg' : 'png',
              quality: exportFormat === 'jpg' ? 0.88 : 1,
              targetDPI: 300,
              includeBackground: true,
              selectedBackground
            }
          );
        } else {
          dataURL = await generateImageFromDesign(designData, { format: exportFormat === 'jpg' ? 'jpg' : 'png', size: 'large' });
          if (!dataURL && target.thumbnail_url) {
            dataURL = target.thumbnail_url;
          }
        }

        // Guarantee output stays strictly under 5 MB limit (4.8 MB limit)
        const compressed = await compressDataUrlToLimit(dataURL, 4800000);
        const ext = exportFormat === 'jpg' ? 'jpg' : 'png';
        const a = document.createElement('a');
        a.href = compressed || dataURL;
        a.download = `${name}.${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setExportOpen(false);
        return;
      }

      if (exportFormat === 'pdf') {
        let frontDataUrl = '';
        let backDataUrl: string | null = null;

        if (isCurrentActive) {
          const graniteConfig = (window as any).__appGraniteConfig;
          const designMode = (window as any).__appDesignMode || '';
          const captureWithBg = async (): Promise<string> => {
            return renderProduction300DPI(
              stageRef.current,
              graniteConfig,
              designMode,
              currentDesign.elements,
              {
                format: 'png',
                quality: 1,
                targetDPI: 300,
                includeBackground: true,
                selectedBackground
              }
            );
          };

          const originalSide = activeSide;
          if (activeSide !== 'front') {
            setActiveSide('front');
            await new Promise(r => setTimeout(r, 400));
          }
          frontDataUrl = await captureWithBg();

          if (isBackSideSupported) {
            setActiveSide('back');
            await new Promise(r => setTimeout(r, 400));
            backDataUrl = await captureWithBg();
          }
          setActiveSide(originalSide);
        } else {
          frontDataUrl = await generateImageFromDesign(designData, { format: 'png', size: 'large' });
          if (!frontDataUrl && target.thumbnail_url) {
            frontDataUrl = target.thumbnail_url;
          }
        }

        await createCompressedPdf(frontDataUrl, backDataUrl, `${name}.pdf`, 4800000);
        setExportOpen(false);
        return;
      }

      if (exportFormat === 'kpro') {
        const bundle = {
          type: 'kpro',
          __kpro: true,
          version: 1,
          app: 'Monu Design',
          savedAt: new Date().toISOString(),
          design: designData,
          background: designData?.background || null,
          graniteMeta: designData?.selectedGraniteMeta || undefined,
          graniteImage: null
        } as any;

        const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/x-kpro+json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${name}.kpro`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setExportOpen(false);
        return;
      }
    } catch (e: any) {
      showError('Export Error', e?.message || 'Failed to export');
    }
  };

  const toggleTool = (tool: string) => {
    const newActiveTools = new Set(activeTools);
    if (newActiveTools.has(tool)) {
      newActiveTools.delete(tool);
    } else {
      newActiveTools.add(tool);
    }
    setActiveTools(newActiveTools);
  };

  // Share functionality
  const handleShare = async (type: 'download' | 'copyLink' | 'email') => {
    if (shareBusy) return;
    setShareBusy(type);
    try {
      const designName = currentDesign.name || 'memorial-design';
      const shareText = `Check out our memorial design: ${designName}`;
      const shareTitle = `Memorial Design: ${designName}`;

      const backendBaseForSharePreview = () => {
        const envBase = process.env.REACT_APP_API_URL;
        if (envBase) {
          try {
            return new URL(envBase).origin;
          } catch {
            return String(envBase).replace(/\/+$/, '');
          }
        }
        try {
          const { protocol, hostname, port } = window.location;
          if (String(port) === '8080') return window.location.origin;
          return `${protocol}//${hostname}:8080`;
        } catch {
          return window.location.origin;
        }
      };

      const buildSharePayload = (compact = false) => {
        const safeClone = (val: any) => {
          try { return JSON.parse(JSON.stringify(val)); } catch { return val; }
        };
        const design: any = safeClone({
          ...currentDesign,
          background: selectedBackground || (currentDesign as any)?.background || null,
          selectedGraniteMeta: selectedGraniteMeta || (currentDesign as any)?.selectedGraniteMeta || null,
          selectedGraniteImage: selectedGraniteImage || null,
          selectedGraniteBaseImage: selectedGraniteBaseImage || null,
        }) || {};

        const sanitizeElement = (el: any) => {
          const next = { ...(el || {}) };
          const p = { ...((next && next.properties) || {}) };
          // Remove runtime-heavy/non-serializable fields.
          delete p.imageEl;
          delete p.imgEl;
          delete p.konvaNode;
          delete p._image;
          // In compact mode, trim large data URLs that can break sharing.
          if (compact && typeof p.src === 'string' && p.src.startsWith('data:')) {
            if (p.src.length > 250000) p.src = '';
          }
          next.properties = p;
          return next;
        };

        design.elements = Array.isArray(design.elements) ? design.elements.map(sanitizeElement) : [];
        if (compact) {
          if (typeof design.selectedGraniteImage === 'string' && design.selectedGraniteImage.startsWith('data:')) {
            design.selectedGraniteImage = null;
          }
          if (typeof design.selectedGraniteBaseImage === 'string' && design.selectedGraniteBaseImage.startsWith('data:')) {
            design.selectedGraniteBaseImage = null;
          }
        }

        return { v: 1, design };
      };

      const buildShareUrl = async () => {
        let payload: any = buildSharePayload(false);

        const u = new URL(window.location.href);
        u.search = '';
        u.hash = '';

        // Prioritize backend sharing for short, reliable links
        try {
          const res = await fetch(buildApiUrl('/api/share'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ payload })
          });
          const json = await res.json().catch(() => ({}));
          if (res.ok && json?.ok && json?.sid) {
            u.searchParams.set('sid', String(json.sid));
            return u.toString();
          }
          // Retry with compact payload when server rejects large payloads
          if (res.status === 413 || String(json?.error || '').toLowerCase().includes('payload too large')) {
            payload = buildSharePayload(true);
            const compactRes = await fetch(buildApiUrl('/api/share'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ payload })
            });
            const compactJson = await compactRes.json().catch(() => ({}));
            if (compactRes.ok && compactJson?.ok && compactJson?.sid) {
              u.searchParams.set('sid', String(compactJson.sid));
              return u.toString();
            }
          }
        } catch (e) {
        }

        if (isClientShareCryptoAvailable()) {
          // Encryption fallback uses compact payload so links are more likely to fit URL limits.
          const token = await encryptSharedDesignToUrlParam(buildSharePayload(true));
          u.searchParams.set('d', token);
          return u.toString();
        }

        // Output specific error if both methods fail
        throw new Error('Failed to create share link. Please try saving the design instead.');
      };

      const copyToClipboard = async (textToCopy: string) => {
        const fallbackCopy = () => {
          const el = document.createElement('textarea');
          el.value = textToCopy;
          el.setAttribute('readonly', '');
          el.style.position = 'fixed';
          el.style.left = '-9999px';
          el.style.top = '0';
          document.body.appendChild(el);
          el.focus();
          el.select();
          const ok = document.execCommand('copy');
          document.body.removeChild(el);
          return ok;
        };

        try {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(textToCopy);
          } else {
            const ok = fallbackCopy();
            if (!ok) throw new Error('Copy failed');
          }
          return true;
        } catch {
          const ok = fallbackCopy();
          return !!ok;
        }
      };

      const createSharePreview = async () => {
        const payload: any = buildSharePayload(true);

        const res = await fetch(buildApiUrl('/api/share'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ payload })
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json?.ok || !json?.sid) {
          throw new Error(json?.error || 'Failed to create share link');
        }
        const sid = String(json.sid);

        const dataUrl = await generatePngDataUrl();
        const up = await fetch(buildApiUrl(`/api/share/${encodeURIComponent(sid)}/image`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dataUrl })
        });
        const upJson = await up.json().catch(() => ({}));
        if (!up.ok || !upJson?.ok) {
          throw new Error(upJson?.error || 'Failed to upload share image');
        }

        const fallbackBase = backendBaseForSharePreview();
        const pageUrl = String(upJson?.page_url || `${fallbackBase}/share/${sid}`);
        const imageUrl = String(upJson?.image_url || `${fallbackBase}/share/${sid}/image`);
        return { sid, pageUrl, imageUrl };
      };

      const generatePngDataUrl = async () => {
        const stage = stageRef?.current;
        if (!stage) throw new Error('Unable to generate image for sharing');

        let dataURL = '';
        const tmpContainer = document.createElement('div');
        tmpContainer.style.position = 'fixed';
        tmpContainer.style.left = '-10000px';
        tmpContainer.style.top = '-10000px';
        document.body.appendChild(tmpContainer);
        try {
          const cloned = stage.clone({ container: tmpContainer });
          try {
            cloned.find('Transformer').forEach((transformer: any) => { transformer.visible(false); });
            cloned.find('Group').forEach((group: any) => {
              if (group.attrs.name === 'selection-group' || group.attrs.name === 'ui-overlay') {
                group.visible(false);
              }
            });
            cloned.find('Rect').forEach((rect: any) => {
              if (rect.attrs.fill === 'rgba(59, 130, 246, 0.2)' || rect.attrs.stroke === 'rgba(59, 130, 246, 0.8)') {
                rect.visible(false);
              }
            });
            cloned.find('Shape').forEach((shape: any) => {
              try {
                const stroke = String(shape?.attrs?.stroke || '').toLowerCase();
                const dash = shape?.attrs?.dash;
                const strokeWidth = Number(shape?.attrs?.strokeWidth || 0);
                const isSelectionStroke = strokeWidth >= 2 && (
                  stroke === '#007bff' ||
                  stroke === '#3b82f6' ||
                  stroke.includes('59, 130, 246') ||
                  stroke.includes('59,130,246') ||
                  stroke.includes('0, 123, 255') ||
                  stroke.includes('0,123,255')
                );
                const hasDash = Array.isArray(dash) && dash.length >= 2;
                if (hasDash && strokeWidth > 0) {
                  try {
                    if (typeof shape?.setAttrs === 'function') {
                      shape.setAttrs({ dash: undefined, stroke: isSelectionStroke ? undefined : shape?.attrs?.stroke, strokeWidth: isSelectionStroke ? 0 : strokeWidth });
                    } else {
                      shape.attrs = { ...(shape.attrs || {}), dash: undefined, stroke: isSelectionStroke ? undefined : shape?.attrs?.stroke, strokeWidth: isSelectionStroke ? 0 : strokeWidth };
                    }
                  } catch { }
                }
              } catch { }
            });
            try {
              const gl = cloned.findOne && (cloned.findOne('#guides-layer') || cloned.findOne('.guides-layer'));
              if (gl) gl.visible(false);
            } catch { }
            dataURL = cloned.toDataURL({
              mimeType: 'image/png',
              quality: 1,
              pixelRatio: 1.5
            });
          } finally {
            cloned.destroy();
          }
        } finally {
          try { document.body.removeChild(tmpContainer); } catch { }
        }

        return dataURL;
      };

      const downloadPngDataUrl = (dataURL: string) => {
        const link = document.createElement('a');
        link.download = `${designName}.png`;
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

      const tryNativeShare = async (builtShareUrl: string) => {
        const navAny = navigator as any;
        if (typeof navAny?.share !== 'function') return false;

        let file: File | undefined;
        try {
          if (typeof navAny?.canShare === 'function') {
            const dataURL = await generatePngDataUrl();
            const blob = await fetch(dataURL).then((r) => r.blob());
            const candidate = new File([blob], `${designName}.png`, { type: 'image/png' });
            try {
              if (navAny.canShare({ files: [candidate] })) {
                file = candidate;
              }
            } catch { }
          }
        } catch { }

        const shareData: any = {
          title: shareTitle,
          text: shareText,
          url: builtShareUrl
        };
        if (file) shareData.files = [file];

        try {
          await navAny.share(shareData);
          return true;
        } catch {
          return false;
        }
      };

      if (type === 'download') {
        setShareOpen(false);
        setExportTarget({
          id: (currentDesign as any)?.id,
          name: (currentDesign as any)?.name || 'design',
          thumbnail_url: (currentDesign as any)?.thumbnail_url,
          design_json: currentDesign
        });
        if (stageRef?.current) {
          try {
            const stage = stageRef.current.getStage ? stageRef.current.getStage() : stageRef.current;
            setExportPreview(stage.toDataURL({ pixelRatio: 0.5 }));
          } catch {
            setExportPreview((currentDesign as any)?.thumbnail_url || null);
          }
        } else {
          setExportPreview((currentDesign as any)?.thumbnail_url || null);
        }
        setExportFormat('png');
        setExportOpen(true);
        return;
      }

      if (type === 'copyLink') {
        const textToCopy = await buildShareUrl();
        if (textToCopy.length > 12000) {
          showError('Sharing Error', 'This design is too large to share as a link. Please save your design and try again.');
          return;
        }
        const ok = await copyToClipboard(textToCopy);
        if (!ok) throw new Error('Copy failed');
        showSuccess('Success', 'Link copied to clipboard!');
        setShareOpen(false);
        return;
      }

      if (type === 'email') {
        const captureSide = async (): Promise<string | null> => {
          if (!stageRef?.current) return null;
          const stage = stageRef.current.getStage ? stageRef.current.getStage() : stageRef.current;
          const sw = stage.width();
          const sh = stage.height();
          const stageDataURL = stageRef.current.toDataURL({ pixelRatio: 1 });
          const canvas = document.createElement('canvas');
          canvas.width = sw; canvas.height = sh;
          const ctx = canvas.getContext('2d')!;
          const bgUrl = selectedBackground?.image_url || null;
          if (bgUrl) {
            const bgImg = new Image();
            bgImg.crossOrigin = 'anonymous';
            await new Promise<void>(res => { bgImg.onload = () => res(); bgImg.onerror = () => res(); bgImg.src = bgUrl; });
            const rBg = bgImg.width / bgImg.height;
            const rCv = sw / sh;
            let dw = sw, dh = sh, dx = 0, dy = 0;
            if (rBg > rCv) { dw = Math.ceil(sh * rBg); dx = Math.floor((sw - dw) / 2); }
            else { dh = Math.ceil(sw / rBg); dy = Math.floor((sh - dh) / 2); }
            ctx.drawImage(bgImg, dx, dy, dw, dh);
          } else {
            ctx.fillStyle = (selectedBackground as any)?.background_color || '#e0e0e0';
            ctx.fillRect(0, 0, sw, sh);
          }
          const stageImg = new Image();
          await new Promise<void>(res => { stageImg.onload = () => res(); stageImg.onerror = () => res(); stageImg.src = stageDataURL; });
          ctx.drawImage(stageImg, 0, 0, sw, sh);
          return canvas.toDataURL('image/jpeg', 0.85);
        };

        let frontBase64: string | null = null;
        let backBase64: string | null = null;

        try {
          const originalSide = activeSide;

          // Capture front side
          if (activeSide !== 'front') {
            setActiveSide('front');
            await new Promise(r => setTimeout(r, 600)); // wait for render
          }
          frontBase64 = await captureSide();

          // Capture back side if supported
          if (isBackSideSupported) {
            setActiveSide('back');
            await new Promise(r => setTimeout(r, 600)); // wait for render
            backBase64 = await captureSide();
          }

          // Restore original side
          setActiveSide(originalSide);
        } catch (e) {
          console.error('Failed to capture design images for email:', e);
        }

        let shareUrl = '';
        try {
          shareUrl = await buildShareUrl();
        } catch { /* skip */ }

        // Open the server-based Send Email modal with the captured base64 image and share URL
        setShareEmailThumb(frontBase64);
        setShareEmailThumbBack(backBase64);
        setShareEmailTo('');
        setShareEmailMsg(shareUrl ? `Check out our memorial design: ${shareUrl}` : '');
        setShareEmailOpen(true);
        setShareOpen(false);
        return;
      }



    } catch (error: any) {
      const msg = String(error?.message || '').trim();
      showError('Sharing Error', msg || 'Error sharing design. Please try again.');
    } finally {
      setShareBusy(null);
    }
  };

  const handleSendShareEmail = async () => {
    if (!shareEmailTo.trim()) return;
    setShareEmailSending(true);
    try {
      const resp = await fetch(buildApiUrl('/api/contact/email-design'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_email: shareEmailTo.trim(),
          design_name: currentDesign.name || 'Untitled Design',
          design_id: currentDesign.id ?? 0,
          message: shareEmailMsg.trim(),
          image_data: shareEmailThumb || null,       // base64 JPEG of the front design
          image_data_back: shareEmailThumbBack || null, // base64 JPEG of the back design
        }),
      });
      const data = await resp.json();
      if (resp.ok && data?.ok) {
        setShareEmailOpen(false);
        setShareEmailThumb(null);
        setShareEmailThumbBack(null);
        showSuccess('Email Sent', `Design sent to ${shareEmailTo.trim()}`);
      } else {
        showError('Email Failed', data?.error || 'Failed to send email. Please try again.');
      }
    } catch (e: any) {
      showError('Email Failed', e?.message || 'Network error. Please try again.');
    } finally {
      setShareEmailSending(false);
    }
  };

  return (
    <ToolbarContainer>

      <LogoSection onClick={() => {
        showConfirm(
          'Save Design',
          'Please save your design before leaving to the landing page.',
          () => {
            window.location.href = '/';
          },
          undefined,
          'Leave',
          'Stay'
        );
      }}>
        <Logo>
          <img src="/logo.png" alt="" style={{ height: 40, width: 'auto' }} />
          Monu Design
        </Logo>
      </LogoSection>

      <ScrollableTools>
        {/* LEFT HAND SIDE: Open, Save, Print, Share, My Design */}
        <ToolGroup style={{ justifyContent: 'flex-start' }}>
          {/* Hidden file input for Open */}
          <input
            ref={openInputRef}
            type="file"
            accept=".kpro,application/json,application/x-kpro+json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const text = String(reader.result || '{}');
                  const data = JSON.parse(text);
                  if (data && (data.type === 'kpro' || data.__kpro === true)) {
                    const bundle = data;
                    const design = bundle.design || {};
                    updateDesign(() => design);
                    try {
                      if (bundle.background) {
                        setSelectedBackground(bundle.background);
                        try { localStorage.setItem('design_tool.background', JSON.stringify(bundle.background)); } catch { }
                      }
                    } catch { }
                    try {
                      if (bundle.graniteMeta) {
                        setSelectedGraniteMeta?.(bundle.graniteMeta);
                      }
                      if (Object.prototype.hasOwnProperty.call(bundle, 'graniteImage')) {
                        setSelectedGraniteImage?.(bundle.graniteImage || null);
                      }
                    } catch { }
                  } else {
                    updateDesign(() => data);
                  }
                } catch { }
              };
              reader.readAsText(file);
            }}
          />
          <ActionButton $variant="info" onClick={() => openInputRef.current?.click()} title="Open Design">
            <FolderOpen size={16} />
            <span>Open</span>
          </ActionButton>

          <ActionButton $variant="success" disabled={saveBusy || saveAndCartBusy} onClick={handleSaveClick} title="Save Design">
            <SaveIcon size={16} />
            <span>Save</span>
          </ActionButton>

          <ActionButton $variant="purple" onClick={() => setPrintPreviewOpen(true)} title="Print Design">
            <Printer size={16} />
            <span>Print</span>
          </ActionButton>

          <ActionButton $variant="teal" onClick={() => setShareOpen(true)} title="Share Design">
            <Share2 size={16} />
            <span>Share</span>
          </ActionButton>

          {isAuthed && (
            <ActionButton $variant="info" onClick={() => {
              setRightPanelOpen(true);
              try { window.dispatchEvent(new CustomEvent('rightpanel:open', { detail: { view: 'designs' } })); } catch { }
            }} title="My Designs">
              <Folder size={16} />
              <span>My Design</span>
            </ActionButton>
          )}
        </ToolGroup>

        {/* CENTRE: Ruler, Guidelines, Undo, Redo, Flip, Background */}
        <ToolGroup style={{ margin: '0 auto' }}>
          <ToolButton
            $active={activeTools.has('ruler')}
            onClick={() => {
              toggleTool('ruler');
              const key = 'design_tool.ruler';
              const next = localStorage.getItem(key) === 'on' ? 'off' : 'on';
              localStorage.setItem(key, next);
              try { window.dispatchEvent(new Event('ruler:toggle')); } catch { }
            }}
            title="Ruler"
          >
            <RulerIcon size={16} />
            <span>Ruler</span>
          </ToolButton>

          <ToolButton
            $active={activeTools.has('guidelines')}
            onClick={() => {
              toggleTool('guidelines');
              try { window.dispatchEvent(new Event('guidelines:toggle')); } catch { }
            }}
            title="Guidelines"
          >
            <Sliders size={16} />
            <span>Guidelines</span>
          </ToolButton>

          <Divider />

          <ToolButton onClick={(!isModelToolActive && !isModelLastChange && canUndo) ? undo : undefined} $active={false} disabled={(!canUndo || isModelToolActive || isModelLastChange) as any} title="Undo">
            <Undo2 size={16} />
            <span>Undo</span>
          </ToolButton>
          <ToolButton onClick={(!isModelToolActive && !isModelLastChange && canRedo) ? redo : undefined} $active={false} disabled={(!canRedo || isModelToolActive || isModelLastChange) as any} title="Redo">
            <Redo2 size={16} />
            <span>Redo</span>
          </ToolButton>

          {isBackSideSupported && (
            <ToolButton
              onClick={() => setActiveSide(activeSide === 'front' ? 'back' : 'front')}
              $active={activeSide === 'back'}
              title="Flip side"
            >
              <RefreshCw size={16} />
              <span>{activeSide === 'front' ? 'Flip to Back' : 'Flip to Front'}</span>
            </ToolButton>
          )}

          <Divider />

          <ToolButton onClick={handleBackgroundClick} title="Background">
            <ImageIcon size={16} />
            <span>Background</span>
          </ToolButton>
        </ToolGroup>

        {/* RIGHT ALIGNED: Save & Add to Cart, My Cart, Logout / Login */}
        <ToolGroup style={{ justifyContent: 'flex-end', marginLeft: 0 }}>
          {canCreateQuotation ? (
            <ActionButton
              $variant="amber"
              disabled={saveBusy || saveAndCartBusy}
              onClick={handleSaveAndAddToCartClick}
              title="Save design & create quotation"
              style={{
                background: '#f59e0b',
                color: '#000000',
                fontWeight: 700,
                boxShadow: '0 2px 6px rgba(245, 158, 11, 0.4)'
              }}
            >
              <ShoppingCart size={16} />
              <span>Save & Add to Cart</span>
            </ActionButton>
          ) : (
            <ActionButton
              $variant="amber"
              disabled={saveBusy || saveAndCartBusy}
              onClick={handleSaveAndAddToCartClick}
              title="Save design"
              style={{
                background: '#3b82f6',
                color: '#ffffff',
                fontWeight: 700,
                boxShadow: '0 2px 6px rgba(59, 130, 246, 0.4)'
              }}
            >
              <SaveIcon size={16} />
              <span>Save Design</span>
            </ActionButton>
          )}

          {!isAuthed ? (
            <ActionButton $variant="pink" onClick={() => setRightPanelOpen(true)}>
              <User size={16} />
              <span>Login</span>
            </ActionButton>
          ) : (
            <>
              {canCreateQuotation && (
                <ActionButton $variant="primary" onClick={() => {
                  (window as any).__internalReload = true;
                  window.location.href = '/customer/dashboard?tab=saved_designs';
                }} title="My Cart">
                  <ShoppingCart size={16} />
                  <span>My Cart</span>
                </ActionButton>
              )}

              <ActionButton $variant="red" onClick={handleLogoutClick} title="Logout">
                <LogOut size={16} />
                <span>Logout</span>
              </ActionButton>
            </>
          )}
        </ToolGroup>
      </ScrollableTools>

      <MobileActionGroup>
        <ToolButton onClick={(!isModelToolActive && !isModelLastChange && canUndo) ? undo : undefined} $active={false} disabled={(!canUndo || isModelToolActive || isModelLastChange) as any}>
          <Undo2 size={18} />
        </ToolButton>
        <ToolButton onClick={(!isModelToolActive && !isModelLastChange && canRedo) ? redo : undefined} $active={false} disabled={(!canRedo || isModelToolActive || isModelLastChange) as any}>
          <Redo2 size={18} />
        </ToolButton>
        {isBackSideSupported && (
          <ToolButton
            onClick={() => setActiveSide(activeSide === 'front' ? 'back' : 'front')}
            $active={activeSide === 'back'}
          >
            <RefreshCw size={18} />
          </ToolButton>
        )}
        <ToolButton onClick={handleBackgroundClick}>
          <ImageIcon size={18} />
        </ToolButton>
        <MobileMenuToggle onClick={() => setMenuOpen(true)}>
          <Menu size={24} />
        </MobileMenuToggle>
      </MobileActionGroup>
      {/* Mobile Drawer */}
      <DrawerOverlay $isOpen={menuOpen} />
      <DrawerSidebar $isOpen={menuOpen}>
        <DrawerHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: '#5D722C', display: 'grid', placeItems: 'center', color: '#fff' }}>
              <User size={18} />
            </div>
            <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}>Menu</span>
          </div>
          <button onClick={() => setMenuOpen(false)} style={{ background: '#f1f5f9', border: 'none', cursor: 'pointer', color: '#64748b', width: 32, height: 32, borderRadius: '9999px', display: 'grid', placeItems: 'center' }}>
            <X size={18} />
          </button>
        </DrawerHeader>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Add Ruler back here since it was removed from toolbar but user might still want it accessible */}
          <DrawerItem $active={activeTools.has('ruler')} onClick={() => {
            toggleTool('ruler');
            const key = 'design_tool.ruler';
            const next = localStorage.getItem(key) === 'on' ? 'off' : 'on';
            localStorage.setItem(key, next);
            try { window.dispatchEvent(new Event('ruler:toggle')); } catch { }
            setMenuOpen(false);
          }}>
            <RulerIcon size={20} />
            <span>Ruler</span>
          </DrawerItem>

          <DrawerItem onClick={() => { openInputRef.current?.click(); setMenuOpen(false); }}>
            <FolderOpen size={20} />
            <span>Open Design</span>
          </DrawerItem>

          <DrawerItem disabled={saveBusy} onClick={() => { handleSaveClick(); setMenuOpen(false); }}>
            <SaveIcon size={20} />
            <span>Save Design</span>
          </DrawerItem>

          <DrawerItem onClick={() => { setPrintPreviewOpen(true); setMenuOpen(false); }}>
            <Printer size={20} />
            <span>Print</span>
          </DrawerItem>

          <DrawerItem onClick={() => { setShareOpen(true); setMenuOpen(false); }}>
            <Share2 size={20} />
            <span>Share</span>
          </DrawerItem>

          <div style={{ height: 1, background: '#f1f5f9', margin: '12px 0' }} />

          {!isAuthed ? (
            <DrawerItem $variant="primary" onClick={() => { setRightPanelOpen(true); setMenuOpen(false); }}>
              <User size={20} />
              <span>Login / Sign Up</span>
            </DrawerItem>
          ) : (
            <>
              <DrawerItem onClick={() => {
                setRightPanelOpen(true);
                try { window.dispatchEvent(new CustomEvent('rightpanel:open', { detail: { view: 'designs' } })); } catch { }
                setMenuOpen(false);
              }}>
                <Folder size={20} />
                <span>My Design</span>
              </DrawerItem>

              {canCreateQuotation && (
                <DrawerItem $variant="primary" onClick={() => {
                  window.location.href = '/customer/dashboard';
                }}>
                  <ShoppingCart size={20} />
                  <span>My Cart</span>
                </DrawerItem>
              )}

              <div style={{ flex: 1 }} />

              <DrawerItem $variant="red" onClick={() => {
                void fetch(buildApiUrl('/api/auth/logout'), {
                  method: 'POST',
                  headers: { ...authHeaders() },
                  keepalive: true
                }).catch(() => { });

                try {
                  localStorage.removeItem('auth_token');
                  localStorage.removeItem('auth_user');
                  localStorage.setItem('logout_signal', Date.now().toString());
                } catch { }
                try { sessionStorage.removeItem('auth_token'); sessionStorage.removeItem('auth_user'); } catch { }
                window.dispatchEvent(new Event('auth:logout'));
                setMenuOpen(false);
              }}>
                <LogOut size={20} />
                <span>Logout</span>
              </DrawerItem>
            </>
          )}
        </div>
      </DrawerSidebar>

      {guideDialogOpen && (
        <div style={{ position: 'fixed', right: 18, bottom: 18, zIndex: 300 }}>
          <div
            style={{
              width: 430,
              borderRadius: 14,
              background: 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(248,250,252,0.95))',
              border: '1px solid #e5e7eb',
              boxShadow: '0 24px 80px rgba(15,23,42,0.28)',
              backdropFilter: 'blur(6px)'
            }}
          >
            <div style={{ position: 'relative', padding: '12px 14px', borderBottom: '1px solid #e5e7eb', background: '#ffffff', borderTopLeftRadius: 14, borderTopRightRadius: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 800, color: '#0f172a', fontSize: 14 }}>New Guide Layout</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Apply margins inside granite</div>
                </div>
                <button onClick={() => setGuideDialogOpen(false)} style={{ border: 'none', background: 'transparent', fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#64748b' }}>×</button>
              </div>
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, background: 'linear-gradient(90deg, #5D722C, #666666)' }} />
            </div>
            <div style={{ padding: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>Top (in)</span>
                  <div style={{ position: 'relative' }}>
                    <input type="number" step={0.25} value={guideTopIn}
                      onChange={(e) => { const v = Number(e.target.value || 0); setGuideTopIn(v); if (guidePreview) try { window.dispatchEvent(new CustomEvent('guidelayout:apply', { detail: { topIn: v, bottomIn: guideBottomIn, leftIn: guideLeftIn, rightIn: guideRightIn } })); } catch { } }}
                      style={{ width: '100%', padding: '10px 36px 10px 12px', border: '2px solid #cbd5e1', borderRadius: 10, outline: 'none' }} />
                    <span style={{ position: 'absolute', right: 10, top: 9, fontSize: 12, color: '#64748b', fontWeight: 700 }}>in</span>
                  </div>
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>Left (in)</span>
                  <div style={{ position: 'relative' }}>
                    <input type="number" step={0.25} value={guideLeftIn}
                      onChange={(e) => { const v = Number(e.target.value || 0); setGuideLeftIn(v); if (guidePreview) try { window.dispatchEvent(new CustomEvent('guidelayout:apply', { detail: { topIn: guideTopIn, bottomIn: guideBottomIn, leftIn: v, rightIn: guideRightIn } })); } catch { } }}
                      style={{ width: '100%', padding: '10px 36px 10px 12px', border: '2px solid #cbd5e1', borderRadius: 10, outline: 'none' }} />
                    <span style={{ position: 'absolute', right: 10, top: 9, fontSize: 12, color: '#64748b', fontWeight: 700 }}>in</span>
                  </div>
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>Bottom (in)</span>
                  <div style={{ position: 'relative' }}>
                    <input type="number" step={0.25} value={guideBottomIn}
                      onChange={(e) => { const v = Number(e.target.value || 0); setGuideBottomIn(v); if (guidePreview) try { window.dispatchEvent(new CustomEvent('guidelayout:apply', { detail: { topIn: guideTopIn, bottomIn: v, leftIn: guideLeftIn, rightIn: guideRightIn } })); } catch { } }}
                      style={{ width: '100%', padding: '10px 36px 10px 12px', border: '2px solid #cbd5e1', borderRadius: 10, outline: 'none' }} />
                    <span style={{ position: 'absolute', right: 10, top: 9, fontSize: 12, color: '#64748b', fontWeight: 700 }}>in</span>
                  </div>
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>Right (in)</span>
                  <div style={{ position: 'relative' }}>
                    <input type="number" step={0.25} value={guideRightIn}
                      onChange={(e) => { const v = Number(e.target.value || 0); setGuideRightIn(v); if (guidePreview) try { window.dispatchEvent(new CustomEvent('guidelayout:apply', { detail: { topIn: guideTopIn, bottomIn: guideBottomIn, leftIn: guideLeftIn, rightIn: v } })); } catch { } }}
                      style={{ width: '100%', padding: '10px 36px 10px 12px', border: '2px solid #cbd5e1', borderRadius: 10, outline: 'none' }} />
                    <span style={{ position: 'absolute', right: 10, top: 9, fontSize: 12, color: '#64748b', fontWeight: 700 }}>in</span>
                  </div>
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                {[
                  { label: '0.50 in', v: 0.5 },
                  { label: '0.75 in', v: 0.75 },
                  { label: '1.00 in', v: 1.0 },
                ].map(p => (
                  <button key={p.label}
                    onClick={() => {
                      setGuideTopIn(p.v); setGuideLeftIn(p.v); setGuideBottomIn(p.v); setGuideRightIn(p.v);
                      if (guidePreview) try { window.dispatchEvent(new CustomEvent('guidelayout:apply', { detail: { topIn: p.v, bottomIn: p.v, leftIn: p.v, rightIn: p.v } })); } catch { }
                    }}
                    style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #cbd5e1', background: '#ffffff', fontSize: 12, fontWeight: 700, color: '#0f172a', cursor: 'pointer' }}
                  >{p.label}</button>
                ))}
              </div>
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#475569' }}>
                  <input type="checkbox" checked={guidePreview} onChange={(e) => setGuidePreview(e.target.checked)} /> Preview
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#475569', opacity: 0.7 }}>
                  <input type="checkbox" checked readOnly /> Center Columns
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#475569' }}>
                  <input type="checkbox" defaultChecked readOnly /> Clear Existing Guides
                </label>
                <div style={{ flex: 1 }} />
                <button
                  onClick={() => {
                    try {
                      localStorage.setItem('guides.topIn', String(guideTopIn));
                      localStorage.setItem('guides.leftIn', String(guideLeftIn));
                      localStorage.setItem('guides.bottomIn', String(guideBottomIn));
                      localStorage.setItem('guides.rightIn', String(guideRightIn));
                    } catch { }
                    try { window.dispatchEvent(new CustomEvent('guidelayout:apply', { detail: { topIn: guideTopIn, bottomIn: guideBottomIn, leftIn: guideLeftIn, rightIn: guideRightIn } })); } catch { }
                    setGuideDialogOpen(false);
                  }}
                  style={{ padding: '10px 16px', background: '#5D722C', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 800, boxShadow: '0 6px 16px rgba(93, 114, 44, 0.35)', cursor: 'pointer' }}
                >
                  OK
                </button>
                <button
                  onClick={() => setGuideDialogOpen(false)}
                  style={{ padding: '10px 16px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: 10, fontWeight: 700, color: '#0f172a', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Background Modal */}
      {bgOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 2000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: 760, maxWidth: '95vw', background: '#fff', borderRadius: 6, boxShadow: '0 14px 34px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', background: '#333333', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 700 }}>
              <span>Add / Replace Background</span>
              <button onClick={() => setBgOpen(false)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 14 }}>
              {bgLoading && null}
              {bgError && <div style={{ padding: 12, color: '#b91c1c', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6 }}>{bgError}</div>}
              {!bgLoading && !bgError && (
                <>
                  {/* Scrollable grid */}
                  <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                      {/* Solid Color Option */}
                      <button
                        onClick={() => setBgSelectedIdx(-1)}
                        style={{ border: bgSelectedIdx === -1 ? '2px solid #5D722C' : '1px solid #e1e5e9', borderRadius: 6, padding: 4, cursor: 'pointer', background: '#fff' }}
                        title="Plain Background"
                      >
                        <div style={{ width: '100%', height: 84, background: '#E0E0E0', borderRadius: 4, position: 'relative' }}>
                          {bgSelectedIdx === -1 && <div style={{ position: 'absolute', inset: 0, border: '2px solid #5D722C', pointerEvents: 'none', borderRadius: 4 }} />}
                        </div>
                        <div style={{ fontSize: 11, marginTop: 4, textAlign: 'center' }}>Default Color</div>
                      </button>

                      {bgList.map((b: any, absoluteIdx: number) => {
                        const isSel = absoluteIdx === bgSelectedIdx;
                        return (
                          <button key={absoluteIdx} onClick={() => setBgSelectedIdx(absoluteIdx)} style={{ border: isSel ? '2px solid #5D722C' : '1px solid #e1e5e9', borderRadius: 6, padding: 4, cursor: 'pointer', background: '#fff' }} title={b.name || 'Background'}>
                            <div style={{ width: '100%', height: 84, background: '#bccddf', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                              <img src={b.thumb_url || b.image_url} alt={b.name || 'Background'} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={(e: any) => { e.currentTarget.style.display = 'none'; }} />
                              {isSel && <div style={{ position: 'absolute', inset: 0, border: '2px solid #5D722C', pointerEvents: 'none', borderRadius: 4 }} />}
                            </div>
                            <div style={{ fontSize: 11, marginTop: 4, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.name || 'Background'}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, gap: 8 }}>
                    <button onClick={() => setBgOpen(false)} style={{ padding: '8px 14px', border: '1px solid #e1e5e9', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Cancel</button>
                    <button onClick={() => {
                      if (bgSelectedIdx == null) return;
                      const chosen = bgSelectedIdx === -1
                        ? { name: 'Default Color', background_color: '#E0E0E0', image_url: null }
                        : bgList[bgSelectedIdx];
                      if (!chosen) return;
                      setSelectedBackground(chosen);
                      try { localStorage.setItem('design_tool.background', JSON.stringify(chosen)); } catch { }
                      setBgOpen(false);
                    }} style={{ padding: '8px 14px', border: 'none', borderRadius: 6, background: '#5D722C', color: '#fff', cursor: 'pointer', fontWeight: 700 }}>OK</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {shareOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 2000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            position: 'fixed',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 460,
            maxWidth: '95vw',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '14px 16px',
              background: 'linear-gradient(135deg, #0f172a 0%, #1f2937 45%, #5D722C 100%)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontWeight: 700
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 30, height: 30, borderRadius: 10, background: 'rgba(255,255,255,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Share2 size={16} color="#ffffff" />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                  <span style={{ fontSize: 14, fontWeight: 800 }}>Share Your Design</span>
                  <span style={{ fontSize: 11, opacity: 0.85, fontWeight: 600 }}>Send to family and friends</span>
                </div>
              </div>
              <button
                onClick={() => setShareOpen(false)}
                style={{
                  background: 'rgba(255,255,255,0.14)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  color: '#fff',
                  cursor: 'pointer',
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={16} color="#ffffff" />
              </button>
            </div>
            <div style={{ padding: '14px 16px 16px 16px' }}>
              <div style={{ marginBottom: 14, textAlign: 'center' }}>
                <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Choose an option</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                <button
                  onClick={() => handleShare('download')}
                  disabled={!!shareBusy}
                  style={{
                    padding: '10px 8px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 14,
                    background: '#fff',
                    cursor: shareBusy ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 8,
                    transition: 'all 0.2s ease',
                    fontSize: 12,
                    fontWeight: 800,
                    color: '#0f172a',
                    minHeight: 80,
                    opacity: shareBusy && shareBusy !== 'download' ? 0.5 : 1
                  }}
                  onMouseOver={(e) => {
                    if (shareBusy) return;
                    e.currentTarget.style.borderColor = '#94a3b8';
                    e.currentTarget.style.boxShadow = '0 10px 24px rgba(15, 23, 42, 0.10)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0px)';
                  }}
                >
                  <div style={{ width: 38, height: 38, borderRadius: 14, background: 'rgba(15, 23, 42, 0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {shareBusy === 'download' ? <SimpleSpinner $color="#0f172a" /> : <Download size={16} color="#0f172a" />}
                  </div>
                  <div>Download</div>
                </button>

                <button
                  onClick={() => handleShare('copyLink')}
                  disabled={!!shareBusy}
                  style={{
                    padding: '10px 8px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 14,
                    background: '#fff',
                    cursor: shareBusy ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 8,
                    transition: 'all 0.2s ease',
                    fontSize: 12,
                    fontWeight: 800,
                    color: '#0f172a',
                    minHeight: 80,
                    opacity: shareBusy && shareBusy !== 'copyLink' ? 0.5 : 1
                  }}
                  onMouseOver={(e) => {
                    if (shareBusy) return;
                    e.currentTarget.style.borderColor = '#2563eb';
                    e.currentTarget.style.boxShadow = '0 10px 24px rgba(37, 99, 235, 0.15)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0px)';
                  }}
                >
                  <div style={{ width: 38, height: 38, borderRadius: 14, background: 'rgba(37, 99, 235, 0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {shareBusy === 'copyLink' ? <SimpleSpinner $color="#2563eb" /> : <Link2 size={16} color="#2563eb" />}
                  </div>
                  <div>Copy Link</div>
                </button>

                <button
                  onClick={() => handleShare('email')}
                  disabled={!!shareBusy}
                  style={{
                    padding: '10px 8px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 14,
                    background: '#fff',
                    cursor: shareBusy ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 8,
                    transition: 'all 0.2s ease',
                    fontSize: 12,
                    fontWeight: 800,
                    color: '#0f172a',
                    minHeight: 80,
                    opacity: shareBusy && shareBusy !== 'email' ? 0.5 : 1
                  }}
                  onMouseOver={(e) => {
                    if (shareBusy) return;
                    e.currentTarget.style.borderColor = '#0ea5e9';
                    e.currentTarget.style.boxShadow = '0 10px 24px rgba(14, 165, 233, 0.14)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0px)';
                  }}
                >
                  <div style={{ width: 38, height: 38, borderRadius: 14, background: 'rgba(14, 165, 233, 0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {shareBusy === 'email' ? <SimpleSpinner $color="#0ea5e9" /> : <Mail size={16} color="#0ea5e9" />}
                  </div>
                  <div>Email</div>
                </button>


              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Modal */}
      {saveOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 2000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: 600, maxWidth: '95vw', background: '#fff', borderRadius: 12, boxShadow: '0 14px 34px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', background: '#333333', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 700 }}>
              <span>Save Design</span>
              <button onClick={() => setSaveOpen(false)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 20, display: 'grid', gridTemplateColumns: saveBackThumb ? '1fr 1fr' : '240px 1fr', gap: 20, alignItems: 'start' }}>
              {saveBackThumb ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, gridColumn: 'span 2' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Front Side</div>
                    <div style={{ width: '100%', aspectRatio: '4/3', background: '#f3f4f6', border: '1px solid #e1e5e9', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                      <img src={saveThumb} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Back Side</div>
                    <div style={{ width: '100%', aspectRatio: '4/3', background: '#f3f4f6', border: '1px solid #e1e5e9', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                      <img src={saveBackThumb} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ width: 240, height: 180, background: '#f3f4f6', border: '1px solid #e1e5e9', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  {saveThumb ? <img src={saveThumb} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} /> : <div style={{ color: '#94a3b8', fontSize: 14, textAlign: 'center' }}>Preview unavailable</div>}
                </div>
              )}
              <div style={{ gridColumn: saveBackThumb ? 'span 2' : 'auto', marginTop: saveBackThumb ? 10 : 0 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 6, fontWeight: 700 }}>Design Name</label>
                <input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Enter design name..." style={{ width: '100%', padding: '12px 14px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, fontWeight: 500, outline: 'none', transition: 'border-color 0.2s' }} onFocus={(e) => e.target.style.borderColor = '#5D722C'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '0 16px 16px 16px' }}>
              <button disabled={saveBusy} onClick={() => setSaveOpen(false)} style={{ padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: saveBusy ? 'not-allowed' : 'pointer', opacity: saveBusy ? 0.7 : 1 }}>Cancel</button>
              <button disabled={saveBusy} onClick={async () => {
                try {
                  setSaveBusy(true);
                  // Check if user is authenticated first
                  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
                  if (!token) {
                    showError('Authentication Required', 'Please log in to save your design. Click "My Design" to sign in.');
                    return;
                  }

                  const pdLastRequired = extractPersonalLastName();
                  if (!pdLastRequired) {
                    showError('Personal Details', 'Please enter your Personal Details before saving.');
                    return;
                  }

                  let name = (saveName || 'Untitled').trim() || 'Untitled';
                  const designId = (currentDesign as any)?.id;

                  const buildDesignJsonForSave = (opts: { name: string; id?: any }) => {
                    const next: any = {
                      ...currentDesign,
                      ...opts,
                      background: selectedBackground,
                      selectedGraniteBaseImage: selectedGraniteBaseImage || (currentDesign as any)?.selectedGraniteBaseImage || null,
                      selectedSizeName: (currentDesign as any)?.selectedSizeName,
                      selectedGraniteMeta: selectedGraniteMeta || (currentDesign as any)?.selectedGraniteMeta
                    } as any;
                    try {
                      const els: any[] = Array.isArray(next.elements) ? next.elements.map((el: any) => ({ ...el, properties: { ...(el?.properties || {}) } })) : [];
                      const tplIdx = els.findIndex((el: any) => el?.type === 'artwork' && (el?.properties?.role === 'template' || el?.properties?.isTemplate === true));
                      if (tplIdx >= 0) {
                        const tpl = els[tplIdx];
                        const src = String(tpl?.properties?.src || next?.selectedGraniteMeta?.src || '');
                        const modelId = Number(tpl?.properties?.modelId || next?.selectedGraniteMeta?.modelId || (currentDesign as any)?.selectedModelId || 0);
                        const sizeId = Number(tpl?.properties?.sizeId ?? next?.selectedGraniteMeta?.sizeId ?? (currentDesign as any)?.selectedSizeId ?? 0);
                        const colorId = Number(tpl?.properties?.colorId || next?.selectedGraniteMeta?.colorId || (currentDesign as any)?.selectedColorId || 0);
                        if (modelId > 0 && sizeId >= 0 && colorId > 0 && src) {
                          next.selectedGraniteMeta = { modelId, sizeId, colorId, src };
                          els[tplIdx] = { ...tpl, properties: { ...tpl.properties, modelId, sizeId, colorId, src } };
                        }
                      } else if (next.selectedGraniteMeta && next.selectedGraniteMeta.modelId > 0 && next.selectedGraniteMeta.src) {
                        next.selectedGraniteMeta = {
                          modelId: Number(next.selectedGraniteMeta.modelId || 0),
                          sizeId: Number(next.selectedGraniteMeta.sizeId || 0),
                          colorId: Number(next.selectedGraniteMeta.colorId || 0),
                          src: String(next.selectedGraniteMeta.src)
                        };
                      }
                      next.elements = els;
                    } catch { }
                    return next;
                  };

                  // Helper: upload pending data-URL images found in currentDesign.elements
                  const uploadPendingImages = async () => {
                    try {
                      const els = (currentDesign as any)?.elements || [];
                      const toUpload: Array<{ el: any; index: number; dataUrl: string }> = [];
                      els.forEach((el: any, idx: number) => {
                        const src = el?.properties?.src;
                        if (typeof src === 'string' && src.startsWith('data:')) {
                          toUpload.push({ el, index: idx, dataUrl: src });
                        }
                      });

                      if (toUpload.length === 0) return;

                      for (const item of toUpload) {
                        try {
                          const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token') || '';
                          const res = await fetch(buildApiUrl('/api/profile/photo'), {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': 'Bearer ' + token } : {}) },
                            body: JSON.stringify({ photo_data_url: item.dataUrl })
                          });
                          const json = await res.json().catch(() => ({}));
                          if (res.ok && json && json.url) {
                            // replace the element src in the design state and currentDesign copy
                            updateDesign((prev: any) => {
                              const nextEls = [...(prev.elements || [])];
                              if (nextEls[item.index]) {
                                nextEls[item.index] = { ...nextEls[item.index], properties: { ...nextEls[item.index].properties, src: json.url } };
                              }
                              return { ...prev, elements: nextEls };
                            });
                            // Also mutate currentDesign object locally for the save payload
                            try { if ((currentDesign as any)?.elements && (currentDesign as any).elements[item.index]) { (currentDesign as any).elements[item.index].properties.src = json.url; } } catch { }
                          } else {
                            throw new Error(json?.error || `Failed to upload image ${item.index + 1}`);
                          }
                        } catch (err) {
                          const msg = (err as any)?.message || 'Failed to upload one of the images before save.';
                          throw new Error(msg);
                        }
                      }
                    } catch (err) {
                      throw err;
                    }
                  };

                  // Upload any pending data-URL images before saving
                  await uploadPendingImages();

                  const hasInlineDataUrls = () => {
                    try {
                      const els = ((currentDesign as any)?.elements || []) as any[];
                      return els.some((el: any) => {
                        const src = el?.properties?.src;
                        return typeof src === 'string' && src.startsWith('data:');
                      });
                    } catch {
                      return false;
                    }
                  };

                  if (hasInlineDataUrls()) {
                    throw new Error('Some images are still inline (data URLs). Please retry save after uploads complete.');
                  }

                  const computeNextSaveAsNewName = async (inputName: string): Promise<string> => {
                    const raw = (inputName || '').trim() || 'Untitled';
                    const m = raw.match(/^(.+?)[-_ ](\d+)$/);
                    const prefix = (m?.[1] ?? raw).trim() || 'Untitled';
                    const fallbackFromParsed = () => {
                      const parsedNum = m?.[2] ? (parseInt(m[2].replace(/^0+/, '') || '0', 10) || 0) : 0;
                      const next = parsedNum > 0 ? parsedNum + 1 : 1;
                      const padded = next < 10 ? `0${next}` : `${next}`;
                      return `${prefix}_${padded}`;
                    };

                    try {
                      const res = await fetch(buildApiUrl('/api/designs'), { headers: { ...authHeaders() } });
                      const data = await res.json().catch(() => ({}));
                      const list = Array.isArray(data?.designs) ? data.designs : (Array.isArray(data?.data) ? data.data : []);

                      const escapedPrefix = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                      const prefixRegex = new RegExp(`^(${escapedPrefix})[-_ ]?(\\d+)$`, 'i');
                      let max = 0;
                      let any = false;

                      for (const d of list) {
                        const n = String(d?.name || '').trim();
                        if (!n) continue;
                        if (n.toLowerCase() === raw.toLowerCase() || n.toLowerCase() === prefix.toLowerCase()) {
                          any = true;
                        }
                        const mm = n.match(prefixRegex);
                        if (mm && mm[2]) {
                          any = true;
                          const num = parseInt(mm[2].replace(/^0+/, '') || '0', 10) || 0;
                          if (num > max) max = num;
                        }
                      }

                      if (!any) return prefix;

                      const next = (max || 0) + 1;
                      const padded = next < 10 ? `0${next}` : `${next}`;
                      return `${prefix}_${padded}`;
                    } catch {
                      return fallbackFromParsed();
                    }
                  };

                  try {
                    const pdLast = extractPersonalLastName();
                    const cleanedLast = (pdLast || '').replace(/[^a-zA-Z0-9_ -]/g, '').trim();
                    const currentDesignName = String((currentDesign as any)?.name || '').trim();
                    const extractBaseName = (value: string): string => {
                      const raw = (value || '').toString().trim();
                      const m = raw.match(/^(.+?)[-_ ](\d+)$/);
                      return (m?.[1] ?? raw).toString().trim();
                    };

                    const inputWasDefault = !currentDesignName || name.toLowerCase() === currentDesignName.toLowerCase();
                    const baseMismatch = Boolean(
                      cleanedLast && extractBaseName(currentDesignName || name).toLowerCase() !== cleanedLast.toLowerCase()
                    );

                    if (inputWasDefault && baseMismatch) {
                      const newName = await computeNextSaveAsNewName(cleanedLast);
                      name = newName;
                      try { setSaveName(newName); } catch { }
                    }
                  } catch { }

                  // If design has an ID (existing design), check if it still exists on server
                  if (designId) {
                    // First, check if the design still exists on the server
                    let designExists = true;
                    try {
                      const checkRes = await fetch(buildApiUrl(`/api/designs/${designId}`), {
                        headers: { ...authHeaders() }
                      });
                      if (checkRes.status === 404) {
                        designExists = false;
                      }
                    } catch (error) {
                      // On network/other errors, assume it exists and let the user choose update vs save-as-new
                    }

                    if (!designExists) {
                      // Design no longer exists, clear the ID and save as new

                      const newName = await computeNextSaveAsNewName(name);
                      updateDesign((prev: any) => ({ ...prev, name: newName, id: undefined }));
                      const designJson = buildDesignJsonForSave({ name: newName, id: undefined });

                      // Prepare metadata for saving
                      const saveData = {
                        name: newName,
                        design_json: designJson,
                        thumbnail_data_url: saveThumb,
                        back_thumbnail_data_url: saveBackThumb,
                        model_id: currentDesign.selectedModelId || null,
                        background_id: selectedBackground?.id || null,
                        canvas_width: 1200, // Default canvas width
                        canvas_height: 600   // Default canvas height (match Designer)
                      };

                      const res = await fetch(buildApiUrl('/api/designs'), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...authHeaders() },
                        body: JSON.stringify(saveData)
                      });
                      const data = await res.json().catch(() => ({}));
                      if (!res.ok || data.ok === false) throw new Error(data.error || 'Save failed');
                      if (data.id) { updateDesign((prev: any) => ({ ...prev, id: data.id })); }

                      setSaveOpen(false);
                      const t = document.createElement('div');
                      t.textContent = 'Saved as New Design';
                      t.style.position = 'fixed'; t.style.right = '16px'; t.style.top = '70px'; t.style.padding = '8px 12px'; t.style.background = '#5D722C'; t.style.color = '#fff'; t.style.borderRadius = '8px'; t.style.boxShadow = '0 6px 16px rgba(0,0,0,0.15)'; t.style.zIndex = '3000';
                      document.body.appendChild(t); setTimeout(() => { try { document.body.removeChild(t); } catch { } }, 1500);
                      return;
                    }

                    const performUpdate = async () => {
                      // Update existing design
                      updateDesign((prev: any) => ({ ...prev, name }));
                      const designJson = buildDesignJsonForSave({ name, id: designId });


                      // Prepare metadata for updating
                      const updateData = {
                        name,
                        design_json: designJson,
                        thumbnail_data_url: saveThumb,
                        back_thumbnail_data_url: saveBackThumb,
                        model_id: currentDesign.selectedModelId || null,
                        background_id: selectedBackground?.id || null,
                        canvas_width: 1200,
                        canvas_height: 600
                      };

                      const res = await fetch(buildApiUrl(`/api/designs/${designId}`), {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', ...authHeaders() },
                        body: JSON.stringify(updateData)
                      });
                      const data = await res.json().catch(() => ({}));
                      if (!res.ok || data.ok === false) throw new Error(data.error || 'Update failed');
                    };

                    const performSaveNew = async () => {
                      // Save as new design (remove ID to create new)
                      const newName = await computeNextSaveAsNewName(name);
                      updateDesign((prev: any) => ({ ...prev, name: newName, id: undefined }));
                      const designJson = buildDesignJsonForSave({ name: newName, id: undefined });


                      // Prepare metadata for saving
                      const saveData = {
                        name: newName,
                        design_json: designJson,
                        thumbnail_data_url: saveThumb,
                        back_thumbnail_data_url: saveBackThumb,
                        model_id: currentDesign.selectedModelId || null,
                        background_id: selectedBackground?.id || null,
                        canvas_width: 1200,
                        canvas_height: 600
                      };

                      const res = await fetch(buildApiUrl('/api/designs'), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...authHeaders() },
                        body: JSON.stringify(saveData)
                      });
                      const data = await res.json().catch(() => ({}));
                      if (!res.ok || data.ok === false) throw new Error(data.error || 'Save failed');
                      if (data.id) { updateDesign((prev: any) => ({ ...prev, id: data.id, is_quoted: false, status: 'saved' })); }
                    };

                    const isQuotedDesign = (currentDesign as any)?.status === 'quoted' || (currentDesign as any)?.is_quoted;
                    if (isQuotedDesign) {
                      await performSaveNew();
                      setSaveOpen(false);
                      showSuccess('Saved as New Design', 'This design was already quoted, so a new design copy was created.');
                      return;
                    }

                    // If we reached here, a design with this ID exists. Ask user whether to update or save as new.
                    showConfirm(
                      'Update Existing Design?',
                      'This design already exists in your saved designs. Do you want to update the existing design or save it as a new design?',
                      async () => { try { await performUpdate(); setSaveOpen(false); } catch (e: any) { showError('Save Error', e?.message || 'Update failed'); } },
                      async () => { try { await performSaveNew(); setSaveOpen(false); } catch (e: any) { showError('Save Error', e?.message || 'Save failed'); } },
                      'Update Existing',
                      'Save as New'
                    );
                    return;
                  } else {
                    // New design - save normally
                    updateDesign((prev: any) => ({ ...prev, name }));
                    const designJson = buildDesignJsonForSave({ name });


                    // Prepare metadata for saving
                    const saveData = {
                      name,
                      design_json: designJson,
                      thumbnail_data_url: saveThumb,
                      back_thumbnail_data_url: saveBackThumb,
                      model_id: currentDesign.selectedModelId || null,
                      background_id: selectedBackground?.id || null,
                      canvas_width: 1200,
                      canvas_height: 600
                    };

                    const res = await fetch(buildApiUrl('/api/designs'), {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', ...authHeaders() },
                      body: JSON.stringify(saveData)
                    });
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok || data.ok === false) throw new Error(data.error || 'Save failed');
                    if (data.id) { updateDesign((prev: any) => ({ ...prev, id: data.id })); }
                  }

                  setSaveOpen(false);
                  const t = document.createElement('div');
                  t.textContent = 'Saved';
                  t.style.position = 'fixed'; t.style.right = '16px'; t.style.top = '70px'; t.style.padding = '8px 12px'; t.style.background = '#5D722C'; t.style.color = '#fff'; t.style.borderRadius = '8px'; t.style.boxShadow = '0 6px 16px rgba(0,0,0,0.15)'; t.style.zIndex = '3000';
                  document.body.appendChild(t); setTimeout(() => { try { document.body.removeChild(t); } catch { } }, 1500);
                } catch (e: any) {
                  showError('Save Error', e?.message || 'Failed to save');
                } finally {
                  setSaveBusy(false);
                }
              }} style={{ padding: '8px 14px', border: 'none', borderRadius: 6, background: '#5D722C', color: '#fff', cursor: saveBusy ? 'not-allowed' : 'pointer', fontWeight: 700, opacity: saveBusy ? 0.85 : 1 }}>Save</button>
            </div>
          </div>
        </div>
      )}



      {/* Right Panel */}
      <RightPanel
        isOpen={rightPanelOpen}
        onClose={() => setRightPanelOpen(false)}
        initialView={undefined}
      />

      {/* Popup */}
      <Popup
        isOpen={popupState.isOpen}
        onClose={hidePopup}
        type={popupState.type}
        title={popupState.title}
        message={popupState.message}
        confirmText={popupState.confirmText}
        cancelText={popupState.cancelText}
        onConfirm={popupState.onConfirm}
        onCancel={popupState.onCancel}
        showCancel={popupState.showCancel}
      />

      <PrintPreviewDialog
        isOpen={printPreviewOpen}
        onClose={() => setPrintPreviewOpen(false)}
        onPrint={async (settings) => {
          // Update the context with the latest print settings
          setPrintSettings(settings);
          // Hide UI elements before printing
          if (stageRef?.current) {
            const stage = stageRef.current;

            // Store original visibility states for UI elements
            const transformers: Array<{ transformer: any; visible: boolean }> = [];
            const selectionGroups: Array<{ group: any; visible: boolean }> = [];
            const guidesLayer: any = (() => {
              try { return stage.findOne('#guides-layer'); } catch { return null; }
            })();
            const guidesLayerVisible: boolean | null = guidesLayer ? !!guidesLayer.visible() : null;

            // Find and hide only Transformers (selection UI)
            stage.find('Transformer').forEach((transformer: any) => {
              transformers.push({ transformer, visible: transformer.visible() });
              transformer.visible(false);
            });

            // Find and hide selection indicators and UI overlays
            stage.find('Group').forEach((group: any) => {
              if (group.attrs.name === 'selection-group' || group.attrs.name === 'ui-overlay') {
                selectionGroups.push({ group, visible: group.visible() });
                group.visible(false);
              }
            });

            // Also hide any selection background rectangles
            stage.find('Rect').forEach((rect: any) => {
              if (rect.attrs.fill === 'rgba(59, 130, 246, 0.2)' ||
                rect.attrs.stroke === 'rgba(59, 130, 246, 0.8)') {
                rect.visible(false);
              }
            });

            if (guidesLayer) {
              try { guidesLayer.visible(false); } catch { }
            }

            // Force a redraw
            stage.batchDraw();

            // Add print-specific styles
            const printStyles = document.createElement('style');
            printStyles.textContent = `
              @media print {
                @page { size: ${settings.orientation || 'landscape'}; margin: 8mm; }
                html, body {
                  margin: 0 !important;
                  padding: 0 !important;
                  width: 100% !important;
                }
                body { background: white !important; }

                /* Hide everything by default */
                body * {
                  visibility: hidden !important;
                }

                /* Print the print preview dialog itself */
                .print-preview-overlay {
                  visibility: visible !important;
                  position: fixed !important;
                  left: 0 !important;
                  top: 0 !important;
                  right: 0 !important;
                  bottom: 0 !important;
                  background: transparent !important;
                  padding: 0 !important;
                  overflow: visible !important;
                  display: flex !important;
                  flex-direction: row !important;
                  justify-content: center !important;
                  align-items: flex-start !important;
                  width: 100% !important;
                }
                .print-preview-dialog,
                .print-preview-dialog * {
                  visibility: visible !important;
                }
                .print-preview-dialog {
                  position: relative !important;
                  width: 100% !important;
                  max-width: 100% !important; /* Full width for print */
                  margin: 0 !important;
                  height: auto !important;
                  border-radius: 0 !important;
                  box-shadow: none !important;
                  overflow: visible !important;
                }
                .print-preview-dialog-content {
                  overflow: visible !important;
                  display: block !important; /* Stack for print */
                }
                [class*="DesignPreview"] {
                  height: auto !important;
                  max-height: none !important;
                  background: white !important;
                  border: none !important;
                  aspect-ratio: ${settings.orientation === 'landscape' ? '11 / 8.5' : '8.5 / 11'} !important;
                  width: 100% !important;
                }
                [class*="DesignImage"] {
                  box-shadow: none !important;
                }
                .print-preview-footer {
                  border: none !important;
                  padding-left: 0 !important;
                  padding-right: 0 !important;
                  margin-top: 20px !important;
                }

                /* Hide non-print UI parts */
                .print-preview-dialog-header {
                  display: none !important;
                }
                .print-preview-settings-section {
                  display: none !important;
                }
                .print-preview-actions {
                  display: none !important;
                }
                .print-preview-preview-section {
                  padding: 0 !important;
                  background: white !important;
                  overflow: visible !important;
                  height: auto !important;
                  max-height: none !important;
                }
                .print-preview-preview-container {
                  box-shadow: none !important;
                  padding: 0 !important;
                  height: auto !important;
                  max-height: none !important;
                }
                .print-preview-preview-container button {
                  display: none !important;
                }

                /* Ensure legacy print template doesn't print */
                .print-template, .print-template * {
                  display: none !important;
                  visibility: hidden !important;
                }
              }
            `;
            document.head.appendChild(printStyles);

            let didRestore = false;
            const restore = () => {
              if (didRestore) return;
              didRestore = true;

              try {
                transformers.forEach(({ transformer, visible }) => {
                  transformer.visible(visible);
                });

                selectionGroups.forEach(({ group, visible }) => {
                  group.visible(visible);
                });

                stage.find('Rect').forEach((rect: any) => {
                  if (rect.attrs.fill === 'rgba(59, 130, 246, 0.2)' ||
                    rect.attrs.stroke === 'rgba(59, 130, 246, 0.8)') {
                    rect.visible(true);
                  }
                });

                if (guidesLayer && guidesLayerVisible !== null) {
                  try { guidesLayer.visible(guidesLayerVisible); } catch { }
                }

                stage.batchDraw();
              } catch { }

              try { document.head.removeChild(printStyles); } catch { }
              try { setPrintPreviewOpen(false); } catch { }
            };

            const afterPrint = () => {
              restore();
              try { window.removeEventListener('afterprint', afterPrint as any); } catch { }
            };

            try { window.addEventListener('afterprint', afterPrint as any); } catch { }

            // Print after a short delay to ensure rendering
            setTimeout(() => {
              window.print();
              // Fallback restore if afterprint doesn't fire
              setTimeout(() => {
                restore();
              }, 2000);
            }, 100);
          } else {
            // Fallback to regular print if no stage ref
            window.print();
            setPrintPreviewOpen(false);
          }
        }}
      />

      {/* Export Modal (centered with preview + options) */}
      {exportOpen && exportTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.55)', backdropFilter: 'blur(4px)', zIndex: 2500 }} onClick={() => setExportOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: 1020, maxWidth: '96vw', background: 'linear-gradient(180deg, #ffffff, #f8fafc)', border: '1px solid #e5e7eb', borderRadius: 20, boxShadow: '0 30px 90px rgba(15,23,42,0.35)', overflow: 'hidden', display: 'grid', gridTemplateColumns: '1fr 380px' }}>
            <div style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)', padding: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              <div style={{ width: '100%', maxWidth: 620, aspectRatio: '4 / 3', borderRadius: 16, overflow: 'hidden', background: 'radial-gradient(circle at 30% 20%, #eef2ff 0%, #e5e7eb 60%, #e2e8f0 100%)', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.04), 0 20px 40px rgba(2,6,23,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {exportPreview ? (
                  <img src={exportPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: 'saturate(1.02) contrast(1.02)' }} onError={(e: any) => { e.currentTarget.style.display = 'none'; }} />
                ) : (
                  <div style={{ color: '#94a3b8', fontWeight: 800, letterSpacing: 0.3 }}>Preview</div>
                )}
              </div>
              <div style={{ position: 'absolute', left: 32, bottom: 30, background: 'rgba(15,23,42,0.65)', color: '#fff', padding: '6px 10px', borderRadius: 10, fontSize: 12, fontWeight: 800, letterSpacing: 0.2, boxShadow: '0 6px 16px rgba(2,6,23,0.35)' }}>
                {exportTarget?.name || 'Design preview'}
              </div>
            </div>
            <div style={{ padding: 16, background: '#ffffff' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 2px 10px 2px', borderBottom: '1px solid #eef2f7' }}>
                <div>
                  <div style={{ fontWeight: 900, color: '#0f172a', fontSize: 16 }}>Exporting 1 design</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Choose a format and share options</div>
                </div>
                <button onClick={() => setExportOpen(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b', width: 36, height: 36, borderRadius: 10, display: 'grid', placeItems: 'center' }} onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#f1f5f9'; }} onMouseOut={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}>
                  ×
                </button>
              </div>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 800, marginTop: 12, marginBottom: 6 }}>Format</div>
              <div style={{ position: 'relative', marginBottom: 12 }}>
                {/* leading format icon */}
                <div style={{ position: 'absolute', left: 12, top: 0, bottom: 0, display: 'grid', placeItems: 'center', color: '#64748b' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <path d="M14 2v6h6"></path>
                  </svg>
                </div>
                <select value={exportFormat} onChange={(e) => setExportFormat(e.target.value as any)}
                  style={{ width: '100%', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', padding: '12px 42px 12px 40px', border: '1px solid #e2e8f0', borderRadius: 5, fontSize: '13px', lineHeight: '1.4', fontWeight: 400, color: '#0f172a', background: 'linear-gradient(180deg,#ffffff,#f8fafc)' }}>
                  <option value="png">PNG — High Quality Image</option>
                  <option value="jpg">JPG — Standard Image</option>
                  <option value="pdf">PDF — Print Quality Document</option>
                  <option value="kpro">.kpro — Editable Project</option>
                </select>
                <div style={{ position: 'absolute', right: 12, top: 0, bottom: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none', color: '#64748b' }}>▾</div>
              </div>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 800, marginTop: 16, marginBottom: 6 }}>Share</div>
              <div style={{ display: 'grid', gap: 10 }}>
                <button onClick={async () => { try { await navigator.clipboard.writeText(window.location.href); showSuccess('Copied', 'Link copied to clipboard'); } catch { showError('Error', 'Could not copy link'); } }}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: '1px solid #e2e8f0', background: 'linear-gradient(180deg,#ffffff,#f8fafc)', borderRadius: 5, fontSize: '13px', lineHeight: '1.4', cursor: 'pointer', transition: 'all .2s ease', boxShadow: '0 2px 6px rgba(2,6,23,0.04)' }}
                  onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#94a3b8'; }}
                  onMouseOut={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#e2e8f0'; }}>
                  <div style={{ width: 28, height: 28, borderRadius: 5, background: '#f5f5f5', display: 'grid', placeItems: 'center', color: '#666666' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                  </div>
                  <span style={{ fontWeight: 400, color: '#0f172a' }}>Get a link</span>
                </button>
                <button onClick={() => { setExportOpen(false); setShareEmailTo(''); setShareEmailMsg(''); setShareEmailOpen(true); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: '1px solid #e2e8f0', background: 'linear-gradient(180deg,#ffffff,#f8fafc)', borderRadius: 5, fontSize: '13px', lineHeight: '1.4', cursor: 'pointer', transition: 'all .2s ease', boxShadow: '0 2px 6px rgba(2,6,23,0.04)' }}
                  onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#94a3b8'; }}
                  onMouseOut={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#e2e8f0'; }}>
                  <div style={{ width: 28, height: 28, borderRadius: 5, background: '#f5f5f5', display: 'grid', placeItems: 'center', color: '#666666' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>
                  <span style={{ fontWeight: 400, color: '#0f172a' }}>Email design</span>
                </button>
              </div>

              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 800, marginTop: 16, marginBottom: 6 }}>Upload</div>
              <div style={{ display: 'grid', gap: 10 }}>
                <button onClick={() => { const u = encodeURIComponent(window.location.href); const q = encodeURIComponent(exportTarget?.name || 'Design'); window.open(`https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${q}`, '_blank', 'width=1000,height=700'); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: '1px solid #e0e0e0', background: 'linear-gradient(180deg,#f5f5f5,#e0e0e0)', color: '#333333', borderRadius: 5, fontSize: '13px', lineHeight: '1.4', cursor: 'pointer', fontWeight: 900, boxShadow: '0 2px 6px rgba(2,6,23,0.04)' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: '#ffffff', display: 'grid', placeItems: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#333333' }}>
                      <path d="M22 12a10 10 0 1 0-11.5 9.9v-7h-2v-2.9h2V9.5c0-2 1.2-3.1 3-3.1.9 0 1.8.1 1.8.1v2h-1c-1 0-1.3.6-1.3 1.2v1.4h2.6L15.1 15h-2v7A10 10 0 0 0 22 12z" />
                    </svg>
                  </div>
                  <span>Facebook</span>
                </button>
                <button onClick={() => { const t = encodeURIComponent((exportTarget?.name || 'Design') + ' ' + window.location.href); window.open(`https://twitter.com/intent/tweet?text=${t}`, '_blank', 'width=1000,height=700'); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: '1px solid #e0e0e0', background: 'linear-gradient(180deg,#f5f5f5,#e0e0e0)', color: '#333333', borderRadius: 5, fontSize: '13px', lineHeight: '1.4', cursor: 'pointer', fontWeight: 900, boxShadow: '0 2px 6px rgba(2,6,23,0.04)' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: '#ffffff', display: 'grid', placeItems: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#333333' }}>
                      <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53A4.48 4.48 0 0 0 22.43.36a9 9 0 0 1-2.83 1.08A4.51 4.51 0 0 0 16.11 0c-2.5 0-4.52 2.06-4.52 4.6 0 .36.04.72.12 1.06C7.69 5.5 4.07 3.67 1.64.9a4.7 4.7 0 0 0-.61 2.31c0 1.6.79 3.01 1.99 3.85a4.43 4.43 0 0 1-2.05-.58v.06c0 2.24 1.55 4.11 3.6 4.53-.38.11-.78.16-1.19.16-.29 0-.57-.03-.84-.08.57 1.86 2.23 3.22 4.2 3.26A9.05 9.05 0 0 1 0 19.54 12.76 12.76 0 0 0 6.92 22c8.3 0 12.84-7.05 12.84-13.17 0-.2 0-.41-.02-.61A9.45 9.45 0 0 0 23 3z" />
                    </svg>
                  </div>
                  <span>Twitter</span>
                </button>
                <button onClick={async () => { if (navigator.share) { try { await navigator.share({ title: exportTarget?.name || 'Design', url: window.location.href }); } catch { } } else { showInfo('Share', 'Use Facebook/Twitter or copy link'); } }}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: '1px solid #e2e8f0', background: 'linear-gradient(180deg,#ffffff,#f8fafc)', color: '#0f172a', borderRadius: 5, fontSize: '13px', lineHeight: '1.4', cursor: 'pointer', fontWeight: 900, boxShadow: '0 2px 6px rgba(2,6,23,0.04)' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: '#f1f5f9', display: 'grid', placeItems: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 8v8M8 12h8" />
                    </svg>
                  </div>
                  <span>More</span>
                </button>
              </div>

              <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={performExportDownload} style={{ padding: '12px 16px', background: '#5D722C', color: '#fff', border: 'none', borderRadius: 5, fontWeight: 900, cursor: 'pointer', boxShadow: '0 8px 20px rgba(93, 114, 44, 0.35)' }}>
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Email Design Modal */}
      {shareEmailOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 4200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShareEmailOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(420px, 90vw)', background: '#fff', borderRadius: 14, boxShadow: '0 16px 40px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 800, fontSize: 16, color: '#0f172a' }}>Send Design by Email</div>
              <button onClick={() => setShareEmailOpen(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b', fontSize: 20 }}>✕</button>
            </div>
            <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {shareEmailThumb && (
                <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    {shareEmailThumbBack && (
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 2 }}>Front Side</div>
                    )}
                    <img src={shareEmailThumb} alt="Front preview" style={{ width: '100%', borderRadius: 8, objectFit: 'cover', maxHeight: 120, border: '1px solid #e2e8f0' }} />
                  </div>
                  {shareEmailThumbBack && (
                    <div style={{ flex: 1, minWidth: 140 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 2 }}>Back Side</div>
                      <img src={shareEmailThumbBack} alt="Back preview" style={{ width: '100%', borderRadius: 8, objectFit: 'cover', maxHeight: 120, border: '1px solid #e2e8f0' }} />
                    </div>
                  )}
                </div>
              )}
              <div style={{ fontSize: 13, color: '#475569' }}>
                The design image above will be sent to the recipient's inbox.
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Recipient Email *</label>
                <input
                  type="email"
                  value={shareEmailTo}
                  onChange={(e) => setShareEmailTo(e.target.value)}
                  placeholder="e.g. client@example.com"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Message (optional)</label>
                <textarea
                  value={shareEmailMsg}
                  onChange={(e) => setShareEmailMsg(e.target.value)}
                  placeholder="Add a personal note…"
                  rows={3}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                <button onClick={() => setShareEmailOpen(false)} style={{ padding: '9px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>Cancel</button>
                <button
                  onClick={handleSendShareEmail}
                  disabled={shareEmailSending || !shareEmailTo.trim()}
                  style={{ padding: '9px 18px', background: shareEmailSending || !shareEmailTo.trim() ? '#93c5fd' : '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, cursor: shareEmailSending || !shareEmailTo.trim() ? 'not-allowed' : 'pointer' }}
                >
                  {shareEmailSending ? 'Sending…' : 'Send Email'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ToolbarContainer>
  );
};

export default Toolbar;

